import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import oddsDataService from '../../utils/services/oddsDataService';
import resultsTrackerService from '../../utils/services/resultsTrackerService';
import AddToTrackerModal from './AddToTrackerModal';
import FiltersModal from './FiltersModal';
import Toast from '../Toast/Toast';
import PriceMovementsExpanded from '../PriceMovements/PriceMovementsExpanded';
import CommunityTipModal from '../common/CommunityTipModal';
import { useAuth } from '../../context/AuthContext';

// Import bookmaker logos
import sportsbetLogo from '../../assets/sb.png';
import tabLogo from '../../assets/tab.jpg';
import pointsbetLogo from '../../assets/pb.png';
import nedsLogo from '../../assets/neds.jpg';
import betrightLogo from '../../assets/betright.jpg';
import bet365Logo from '../../assets/bet365.png';
import betmakersLogo from '../../assets/betmakers.png';
import ladbrokesLogo from '../../assets/ladbrokes.webp';
import fanduelLogo from '../../assets/fanduel.jpg';
import draftkingsLogo from '../../assets/draftkings.jpg';
import betmgmLogo from '../../assets/betmgm.png';
import dabbleLogo from '../../assets/dabble.webp';
import pinnacleLogo from '../../assets/pn_logo.jpeg';
import tabtouchLogo from '../../assets/tabtouch.webp';

const SPORT_OPTIONS = [
    { value: 'nba', label: 'NBA' },
    { value: 'afl', label: 'AFL' },
];

const STRATEGY_OPTIONS_BY_SPORT = {
    nba: [
        { value: 'player_line', label: 'EV' },
        { value: 'market_value', label: 'Market Value' },
        { value: 'sb_baseline', label: 'Market Value - SB Baseline' },
        { value: 'arbs', label: 'Arbs' },
    ],
    afl: [
        { value: 'market_and_implied_ev', label: 'Market & Implied EV' },
    ],
};

const DEFAULT_STRATEGY_BY_SPORT = {
    nba: 'player_line',
    afl: 'market_and_implied_ev',
};

const MV_STRATEGIES = new Set(['market_value', 'sb_baseline', 'market_and_implied_ev']);

const DEVIG_OPTIONS = [
    { value: 'snipeaio', label: 'SnipeAIO' },
    { value: 'sportsbet', label: 'Sportsbet' },
    { value: 'draftkings', label: 'DraftKings' },
    { value: 'pinnacle', label: 'Pinnacle' },
    { value: 'sb_dk', label: 'SB+DK' },
    { value: 'sb_dk_pn', label: 'SB+DK+PN' },
];

const COMBO_DEVIG_BOOKMAKERS = {
    sb_dk: ['sportsbet', 'draftkings'],
    sb_dk_pn: ['sportsbet', 'draftkings', 'pinnacle'],
};

const US_BOOKMAKERS = new Set(['fanduel', 'draftkings', 'betmgm', 'pinnacle']);

const BOOKMAKER_LOGOS = {
    'sportsbet': sportsbetLogo,
    'tab': tabLogo,
    'pointsbet': pointsbetLogo,
    'neds': nedsLogo,
    'betright': betrightLogo,
    'bet365': bet365Logo,
    'betmakers': betmakersLogo,
    'ladbrokes': ladbrokesLogo,
    'fanduel': fanduelLogo,
    'draftkings': draftkingsLogo,
    'betmgm': betmgmLogo,
    'dabble': dabbleLogo,
    'pinnacle': pinnacleLogo,
    'tabtouch': tabtouchLogo,
};

const BOOKMAKER_DISPLAY_NAMES = {
    'sportsbet': 'Sportsbet',
    'tab': 'TAB',
    'pointsbet': 'Pointsbet',
    'neds': 'Neds',
    'betright': 'Betright',
    'bet365': 'Bet365',
    'betmakers': 'Betmakers',
    'ladbrokes': 'Ladbrokes',
    'fanduel': 'FanDuel',
    'draftkings': 'DraftKings',
    'betmgm': 'BetMGM',
    'dabble': 'Dabble',
    'pinnacle': 'Pinnacle',
    'tabtouch': 'TabTouch',
};

function formatStartTime(isoString) {
    const date = new Date(isoString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const isTomorrow = date.toDateString() === tomorrow.toDateString();

    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    if (isToday) return `Today ${timeStr}`;
    if (isTomorrow) return `Tomorrow ${timeStr}`;

    const dateStr = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    return `${dateStr} ${timeStr}`;
}

function formatOdds(odds) {
    if (odds === null || odds === undefined) return '-';
    return Number(odds).toFixed(2);
}

const AFL_ABBREVIATIONS = {
    'adelaide': 'ADEL', 'adelaide crows': 'ADEL', 'crows': 'ADEL',
    'brisbane': 'BRIS', 'brisbane lions': 'BRIS', 'lions': 'BRIS',
    'carlton': 'CARL', 'blues': 'CARL',
    'collingwood': 'COLL', 'magpies': 'COLL',
    'essendon': 'ESS', 'bombers': 'ESS',
    'fremantle': 'FRE', 'dockers': 'FRE',
    'geelong': 'GEEL', 'geelong cats': 'GEEL', 'cats': 'GEEL',
    'gold coast': 'GC', 'gold coast suns': 'GC', 'suns': 'GC',
    'gws': 'GWS', 'gws giants': 'GWS', 'giants': 'GWS', 'greater western sydney': 'GWS',
    'hawthorn': 'HAW', 'hawks': 'HAW',
    'melbourne': 'MELB', 'demons': 'MELB',
    'north melbourne': 'NM', 'kangaroos': 'NM',
    'port adelaide': 'PORT', 'power': 'PORT',
    'richmond': 'RICH', 'tigers': 'RICH',
    'st kilda': 'STK', 'saints': 'STK',
    'sydney': 'SYD', 'sydney swans': 'SYD', 'swans': 'SYD',
    'west coast': 'WCE', 'west coast eagles': 'WCE', 'eagles': 'WCE',
    'western bulldogs': 'WB', 'bulldogs': 'WB',
};

function shortenMatchName(match, sportKey) {
    if (!match) return '';
    const parts = match.split(' vs ');
    if (parts.length !== 2) return match;

    if (sportKey === 'afl') {
        const abbr = (team) => AFL_ABBREVIATIONS[team.trim().toLowerCase()] || team.trim();
        return `${abbr(parts[0])} v ${abbr(parts[1])}`;
    }

    const getLastWord = (team) => {
        const words = team.trim().split(' ');
        return words[words.length - 1];
    };
    return `${getLastWord(parts[0])} vs ${getLastWord(parts[1])}`;
}

function formatLineWithSelection(line, selection) {
    if (!line) return '-';
    const prefix = selection === 'OVER' ? 'o' : selection === 'UNDER' ? 'u' : '';
    return `${prefix}${line}`;
}

function BookmakerLogo({ bookmaker, size = 20 }) {
    const logo = BOOKMAKER_LOGOS[bookmaker];
    const displayName = BOOKMAKER_DISPLAY_NAMES[bookmaker] || bookmaker;

    if (logo) {
        return (
            <img
                src={logo}
                alt={displayName}
                style={{ width: size, height: size }}
                className="rounded object-cover"
                title={displayName}
            />
        );
    }
    return <span className="text-[9px] font-mono text-slate-500 uppercase tracking-wider" title={displayName}>{displayName.substring(0, 3)}</span>;
}

function BookmakerLink({ bookmaker, urls, children }) {
    const url = urls?.[bookmaker];
    if (url) {
        return (
            <a href={url} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                {children}
            </a>
        );
    }
    return children;
}

// Sorted list of known bookmakers for consistent column ordering
const KNOWN_BOOKMAKERS = ['fanduel', 'draftkings', 'pinnacle', 'sportsbet', 'bet365', 'tab', 'tabtouch', 'pointsbet', 'betright', 'betmakers', 'neds', 'betmgm', 'dabble'];

const HIGHLIGHT_DURATION = 8000;

function getBetKey(bet, strategy) {
    if (strategy === 'player_line') {
        return `${bet.player}|${bet.match}|${bet.marketType}|${bet.line}|${bet.selection}`;
    }
    if (strategy === 'arbs') {
        return `${bet.player}|${bet.match}|${bet.marketType}|${bet.line}`;
    }
    return `${bet.player}|${bet.match}|${bet.market}`;
}

const AFL_SEASON_OPTIONS = [
    { value: '2026', label: '2026' },
    { value: 'combined', label: '2025 + 2026' },
];

const AFL_PROP_TYPE_OPTIONS = [
    { value: 'all', label: 'All Props' },
    { value: 'disposals', label: 'Disposals' },
    { value: 'goals', label: 'Goals' },
    { value: 'marks', label: 'Marks' },
    { value: 'tackles', label: 'Tackles' },
    { value: 'fantasy_points', label: 'Fantasy Points' },
];

// AFL market string → canonical prop type. Keywords are mutually exclusive
// across the supported AFL markets (e.g. "1+ Goal", "25+ Disposals",
// "60+ Fantasy Points"), so a simple substring check is sufficient.
function getAflPropTypeFromMarket(market) {
    const m = (market || '').toLowerCase();
    if (m.includes('disposal')) return 'disposals';
    if (m.includes('fantasy')) return 'fantasy_points';
    if (m.includes('goal')) return 'goals';
    if (m.includes('mark')) return 'marks';
    if (m.includes('tackle')) return 'tackles';
    return null;
}

// AFL implied odds/edge come straight from the API for 2026 (the default
// season view). For the "2025 + 2026" view the API doesn't pre-compute the
// combined values, so we sum line_hit + total_games across both seasons and
// re-derive implied_odds (= 1 / combined_hit_rate) and implied_ev as the
// percentage-point gap between the combined hit rate and the implied
// probability of the bet's current best price (matches the backend formula:
// `hit_rate_percentage - (100 / currentOdds)`).
function getImpliedFields(opp, seasonView, bestOdds) {
    if (!opp) {
        return { lineHit: null, totalGames: null, hitRatePercentage: null, impliedOdds: null, impliedEv: null };
    }

    if (seasonView === 'combined') {
        const lineHit2025 = Number.isFinite(opp['2025_line_hit']) ? opp['2025_line_hit'] : 0;
        const lineHit2026 = Number.isFinite(opp['2026_line_hit']) ? opp['2026_line_hit'] : 0;
        const total2025 = Number.isFinite(opp['2025_total_games']) ? opp['2025_total_games'] : 0;
        const total2026 = Number.isFinite(opp['2026_total_games']) ? opp['2026_total_games'] : 0;

        const lineHit = lineHit2025 + lineHit2026;
        const totalGames = total2025 + total2026;

        if (totalGames <= 0) {
            return { lineHit: 0, totalGames: 0, hitRatePercentage: null, impliedOdds: null, impliedEv: null };
        }

        const hitRate = lineHit / totalGames;
        const hitRatePercentage = hitRate * 100;
        const impliedOdds = hitRate > 0 ? 1 / hitRate : null;
        const impliedEv = (Number.isFinite(bestOdds) && bestOdds > 0)
            ? hitRatePercentage - (100 / bestOdds)
            : null;

        return { lineHit, totalGames, hitRatePercentage, impliedOdds, impliedEv };
    }

    // Default: 2026 fields, read directly from the API.
    return {
        lineHit: opp['2026_line_hit'] ?? null,
        totalGames: opp['2026_total_games'] ?? null,
        hitRatePercentage: opp['2026_hit_rate_percentage'] ?? null,
        impliedOdds: opp['2026_implied_odds'] ?? null,
        impliedEv: opp['2026_implied_ev'] ?? null,
    };
}

// Mirrors the existing 2025 guard: drop 0%/100% implied odds since they're
// the "always loses" / "always wins" sentinels and pollute sorting + edges.
function isImpliedValid({ impliedOdds }) {
    return impliedOdds != null && impliedOdds !== 0 && impliedOdds !== 100;
}

function computeChanges(prevBets, newBets, strategy) {
    if (!prevBets || prevBets.length === 0) return { changes: {}, newKeys: new Set() };

    const prevMap = {};
    prevBets.forEach(bet => {
        prevMap[getBetKey(bet, strategy)] = bet;
    });

    const changes = {};
    const newKeys = new Set();

    newBets.forEach(bet => {
        const key = getBetKey(bet, strategy);
        const prev = prevMap[key];

        if (!prev) {
            newKeys.add(key);
            return;
        }

        const betChanges = {};

        if (strategy === 'player_line') {
            if (prev.expectedValue !== bet.expectedValue) {
                betChanges.expectedValue = bet.expectedValue > prev.expectedValue ? 'up' : 'down';
            }
            if (prev.bestOdds !== bet.bestOdds) {
                betChanges.bestOdds = bet.bestOdds > prev.bestOdds ? 'up' : 'down';
            }
            if (prev.line !== bet.line) {
                betChanges.line = bet.line > prev.line ? 'up' : 'down';
            }
            if (prev.avgFairOdds !== bet.avgFairOdds) {
                betChanges.avgFairOdds = bet.avgFairOdds > prev.avgFairOdds ? 'up' : 'down';
            }
            if (prev.impliedProbability !== bet.impliedProbability) {
                const prevNum = parseFloat(prev.impliedProbability);
                const newNum = parseFloat(bet.impliedProbability);
                if (!isNaN(prevNum) && !isNaN(newNum) && prevNum !== newNum) {
                    betChanges.impliedProbability = newNum > prevNum ? 'up' : 'down';
                }
            }

            const prevBkMap = {};
            prev.bookmakerData?.forEach(bd => { prevBkMap[bd.bookmaker] = bd; });
            bet.bookmakerData?.forEach(bd => {
                const prevBd = prevBkMap[bd.bookmaker];
                if (prevBd) {
                    const selKey = bet.selection === 'OVER' ? 'over_price' : 'under_price';
                    if (prevBd[selKey] !== bd[selKey]) {
                        betChanges[`bk_${bd.bookmaker}`] = bd[selKey] > prevBd[selKey] ? 'up' : 'down';
                    }
                }
            });
        }

        if (MV_STRATEGIES.has(strategy)) {
            const prevOpp = prev.valueOpportunities?.[0];
            const newOpp = bet.valueOpportunities?.[0];
            if (prevOpp && newOpp) {
                if (prevOpp.impliedEdgePercentage !== newOpp.impliedEdgePercentage) {
                    betChanges.edgePercentage = newOpp.impliedEdgePercentage > prevOpp.impliedEdgePercentage ? 'up' : 'down';
                }
                if (prevOpp.odds !== newOpp.odds) {
                    betChanges.bestOdds = newOpp.odds > prevOpp.odds ? 'up' : 'down';
                }
                if (prevOpp.avgMarketOdds !== newOpp.avgMarketOdds) {
                    betChanges.avgMarketOdds = newOpp.avgMarketOdds > prevOpp.avgMarketOdds ? 'up' : 'down';
                }
            }
            const prevBkOddsMap = {};
            prev.bookmakerOdds?.forEach(bo => { prevBkOddsMap[bo.bookmaker] = bo; });
            bet.bookmakerOdds?.forEach(bo => {
                const prevBo = prevBkOddsMap[bo.bookmaker];
                if (prevBo && prevBo.odds !== bo.odds) {
                    betChanges[`bk_${bo.bookmaker}`] = bo.odds > prevBo.odds ? 'up' : 'down';
                }
            });
        }

        if (strategy === 'arbs') {
            if (prev.arbPercentage !== bet.arbPercentage) {
                betChanges.arbPercentage = bet.arbPercentage > prev.arbPercentage ? 'up' : 'down';
            }
            if (prev.overOdds !== bet.overOdds) {
                betChanges.overOdds = bet.overOdds > prev.overOdds ? 'up' : 'down';
            }
            if (prev.underOdds !== bet.underOdds) {
                betChanges.underOdds = bet.underOdds > prev.underOdds ? 'up' : 'down';
            }
        }

        if (Object.keys(betChanges).length > 0) {
            changes[key] = betChanges;
        }
    });

    return { changes, newKeys };
}

function ChangeIndicator({ direction }) {
    if (direction === 'up') {
        return (
            <svg className="w-3 h-3 text-emerald-400 inline-block ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 15l7-7 7 7" />
            </svg>
        );
    }
    return (
        <svg className="w-3 h-3 text-red-400 inline-block ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" />
        </svg>
    );
}

function getChangeColor(direction) {
    return direction === 'up' ? 'text-emerald-400' : 'text-red-400';
}

function CountdownRing({ countdown, total }) {
    const progress = ((total - countdown) / total) * 100;
    const circumference = 2 * Math.PI * 11;
    const strokeOffset = circumference - (progress / 100) * circumference;

    return (
        <div className="relative w-8 h-8 flex items-center justify-center">
            <svg className="w-8 h-8 -rotate-90" viewBox="0 0 24 24">
                <circle
                    cx="12" cy="12" r="11"
                    fill="none" stroke="currentColor" strokeWidth="1"
                    className="text-slate-800"
                />
                <circle
                    cx="12" cy="12" r="11"
                    fill="none" stroke="currentColor" strokeWidth="1.5"
                    className="text-emerald-500 transition-all duration-1000 ease-linear"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeOffset}
                    strokeLinecap="round"
                />
            </svg>
            <span className="absolute text-[9px] font-mono font-medium text-emerald-500/80 tabular-nums">
                {countdown}
            </span>
        </div>
    );
}

// Pill-style toggle button used for header controls (Live Feed, Devigged, etc.).
// Visually matches Hot Props' SortPill/Filters button family — emerald accent when on.
function TogglePill({ active, onClick, label, count, title, ariaLabel, className = '' }) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={title}
            aria-pressed={active}
            aria-label={ariaLabel || label}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded border transition-colors whitespace-nowrap ${
                active
                    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                    : 'border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-600'
            } ${className}`}
        >
            <span
                aria-hidden="true"
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    active ? 'bg-emerald-400 animate-pulse' : 'bg-slate-600'
                }`}
            />
            <span>{label}</span>
            {count != null && (
                <span className="ml-0.5 min-w-[16px] h-4 px-1 rounded-full bg-emerald-400 text-emerald-950 text-[9px] font-mono font-bold leading-none inline-flex items-center justify-center tabular-nums">
                    {count}
                </span>
            )}
        </button>
    );
}

// Vertical hairline used in the desktop header to separate logical groups of controls.
function HeaderDivider() {
    return <div aria-hidden="true" className="w-px h-5 bg-slate-700/50" />;
}

// Match strings already arrive in "Team A vs Team B" form; keeping this behind
// a helper so we can adjust formatting in one place if needed.
function formatGameLabel(match) {
    if (!match) return 'Unknown';
    return match;
}

// Compact `<select>` styled to match the HotProps `GameSelect` so the same
// game filter feels identical across the two surfaces. When a real game is
// chosen we tint the field emerald to keep the active filter visible at a
// glance (the field itself sits inside a busy command bar).
function GameSelect({ value, options, onChange, placeholder = 'All games' }) {
    const isAll = value === 'all';
    const selectedLabel = isAll ? placeholder : formatGameLabel(value);
    return (
        <div className="relative">
            <span
                aria-hidden="true"
                className={`pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 ${
                    isAll ? 'text-slate-600' : 'text-emerald-400'
                }`}
            >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
            </span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                aria-label="Filter by game"
                title={selectedLabel}
                className={`appearance-none pl-7 pr-7 py-1.5 bg-slate-800 border rounded text-xs font-mono cursor-pointer hover:border-slate-600 focus:outline-none focus:border-emerald-500 transition-colors max-w-[180px] truncate ${
                    isAll
                        ? 'border-slate-700 text-slate-300'
                        : 'border-emerald-500/40 bg-emerald-500/5 text-emerald-200'
                }`}
            >
                <option value="all">All games</option>
                {options.map((m) => (
                    <option key={m} value={m}>
                        {formatGameLabel(m)}
                    </option>
                ))}
            </select>
            <svg
                aria-hidden="true"
                className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
            >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
        </div>
    );
}

// Small info-circle that reveals a slate tooltip card on hover or keyboard
// focus. CSS-only (matches the SideNav `CollapsedTooltip` pattern) so it
// works without any JS state. The wrapper is focusable for accessibility,
// and `align` lets the consumer flip the tooltip to the right edge so it
// doesn't overflow when used near the trailing edge of a container.
function InfoTooltip({ title, children, align = 'left', side = 'bottom' }) {
    const sideCls = side === 'top' ? 'bottom-full mb-1.5' : 'top-full mt-1.5';
    const alignCls = align === 'right'
        ? 'right-0'
        : align === 'center'
            ? 'left-1/2 -translate-x-1/2'
            : 'left-0';
    return (
        <span
            tabIndex={0}
            role="button"
            aria-label={`${title}: more info`}
            className="relative group inline-flex items-center cursor-help text-slate-500 hover:text-slate-300 focus:text-slate-300 focus:outline-none transition-colors"
        >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeWidth="1.5" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 16v-4M12 8h.01" />
            </svg>
            <span
                role="tooltip"
                className={`pointer-events-none absolute ${sideCls} ${alignCls} z-[100]
                    w-64 max-w-[calc(100vw-1rem)] px-3 py-2.5 rounded-lg
                    bg-slate-900/95 backdrop-blur border border-slate-700/60 shadow-xl shadow-black/40
                    whitespace-normal break-words normal-case tracking-normal
                    opacity-0 translate-y-1
                    group-hover:opacity-100 group-hover:translate-y-0
                    group-focus:opacity-100 group-focus:translate-y-0
                    transition-all duration-150`}
            >
                {title && (
                    <span className="block text-[10px] font-mono font-semibold uppercase tracking-widest text-emerald-400 mb-1.5">
                        {title}
                    </span>
                )}
                <span className="block text-[11px] font-mono font-normal text-slate-300 leading-relaxed">
                    {children}
                </span>
            </span>
        </span>
    );
}

// Merged-column header for the Player·Game·Start cell. Visually labelled just "Player"
// (sortable on player name) so it matches every other header token in the table.
// Game and Start are still ordered by the merged cell's data, but we keep `player`
// as the canonical sort axis for the column to avoid an inconsistent visual "tabs" feel.
function BetPropHeader({ sortConfig, onSort }) {
    const isActive = sortConfig.key === 'player';
    return (
        <th
            onClick={() => onSort('player')}
            className="px-3 py-3 text-left whitespace-nowrap cursor-pointer transition-colors"
        >
            <div className="flex items-center gap-1 text-[10px] font-mono font-semibold uppercase tracking-widest text-slate-500 hover:text-white">
                Player
                {!isActive ? (
                    <svg className="w-3 h-3 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                    </svg>
                ) : sortConfig.direction === 'asc' ? (
                    <svg className="w-3 h-3 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                    </svg>
                ) : (
                    <svg className="w-3 h-3 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                )}
            </div>
        </th>
    );
}

// Pill-style badge for primary metric columns (EV%, Edge%, Arb%, Implied EV%).
// Mirrors the Hot Props "movement" chip — tinted background, hairline border,
// directional arrow, mono tabular numerals. Tone defaults to emerald for positive,
// red for negative.
function MetricBadge({ value, formatted, tone, showArrow = true }) {
    if (value == null || Number.isNaN(value)) {
        return <span className="text-slate-700 font-mono text-[11px]">—</span>;
    }
    const inferredTone = tone || (value < 0 ? 'red' : 'emerald');
    const styles = inferredTone === 'red'
        ? 'bg-red-500/10 border-red-500/30 text-red-400'
        : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400';
    const isPositive = value > 0;
    return (
        <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border ${styles}`}>
            {showArrow && (
                <svg
                    className="w-2.5 h-2.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                >
                    {isPositive ? (
                        <path d="M12 19V5M5 12l7-7 7 7" />
                    ) : (
                        <path d="M12 5v14M5 12l7 7 7-7" />
                    )}
                </svg>
            )}
            <span className="text-[11px] font-mono font-bold tabular-nums leading-none">
                {formatted}
            </span>
        </div>
    );
}

// Merged-column body cell: prominent player name + meta subline (game · start),
// matching the Hot Props "Prop" column. Uses sans-serif for the player and font-mono
// for the metadata, just like Hot Props' HotPropRow.
function BetPropCell({ player, match, startTime, sport, mobileMarket }) {
    return (
        <td className="px-3 py-2.5 max-w-[200px] md:max-w-[260px]">
            <div className="flex flex-col min-w-0">
                <span className="text-xs font-semibold text-slate-300 truncate" title={player}>
                    {player}
                </span>
                <span className="text-[10px] font-mono text-slate-500 uppercase tracking-wider truncate">
                    {mobileMarket && (
                        <span className="md:hidden">{mobileMarket} · </span>
                    )}
                    {shortenMatchName(match, sport)}
                    <span className="text-slate-700"> · </span>
                    <span className="text-slate-600">{formatStartTime(startTime)}</span>
                </span>
            </div>
        </td>
    );
}

function SnipeFeed() {
    const { hasRole } = useAuth();
    const canTip = hasRole('COMMUNITY_TIPPER');
    const [sport, setSport] = useState('afl');
    const [strategy, setStrategy] = useState(DEFAULT_STRATEGY_BY_SPORT.afl);
    const [oddsData, setOddsData] = useState(null);
    const [meta, setMeta] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });
    const [trackerModalBetKey, setTrackerModalBetKey] = useState(null);
    const [tipModalBet, setTipModalBet] = useState(null);
    // AFL-only: bet keys whose price-movements panel is currently expanded.
    // We key by `getBetKey(bet, strategy)` (which for AFL is `${player}|${match}|${market}`),
    // matching how the rest of SnipeFeed identifies bets across refreshes.
    const [expandedBetKeys, setExpandedBetKeys] = useState(() => new Set());
    // AFL-only: which season's implied stats to display/sort/filter on.
    // Default '2026' uses the API fields directly; 'combined' sums 2025+2026
    // line hits & total games and re-derives implied odds + edge on the client.
    const [aflSeasonView, setAflSeasonView] = useState('2026');
    // AFL-only: prop type filter (Disposals / Goals / Marks / Tackles / Fantasy
    // Points). Defaults to 'all' which leaves the dataset untouched.
    const [aflPropType, setAflPropType] = useState('all');
    // Game filter (e.g. "Carlton vs Collingwood"). Defaults to 'all'. The list
    // of options is derived from the current oddsData so it always reflects
    // exactly what the user can choose between right now.
    const [selectedGame, setSelectedGame] = useState('all');
    const [devig, setDevig] = useState('snipeaio');
    const [searchTerm, setSearchTerm] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [arbStake, setArbStake] = useState(100);
    const [evFilters, setEvFilters] = useState({
        minBookmakers: { enabled: true, value: 5 },
        minEv: { enabled: true, value: 3 },
        startWithin: { enabled: false, value: 3 },
        excludeBestBookmakers: { enabled: false, value: [] },
    });
    const [mvFilters, setMvFilters] = useState({
        minBookmakers: { enabled: true, value: 4 },
        minEv: { enabled: true, value: 10.5 },
        startWithin: { enabled: false, value: 3 },
        excludeBestBookmakers: { enabled: false, value: [] },
    });
    const [sbFilters, setSbFilters] = useState({
        minBookmakers: { enabled: false, value: 2 },
        minEv: { enabled: true, value: 10 },
        startWithin: { enabled: false, value: 3 },
        excludeBestBookmakers: { enabled: false, value: [] },
    });
    const [arbFilters, setArbFilters] = useState({
        minArb: { enabled: true, value: 2 },
        excludeBookmakers: { enabled: false, value: [] },
    });
    const [aflMvFilters, setAflMvFilters] = useState({
        minBookmakers: { enabled: true, value: 4 },
        minEv: { enabled: true, value: 10 },
        minImpliedEv: { enabled: false, value: 0 },
        minOdds: { enabled: false, value: 1.50 },
        minSampleSize: { enabled: false, value: 5 },
        startWithin: { enabled: false, value: 3 },
        excludeBestBookmakers: { enabled: false, value: [] },
    });
    const filters = strategy === 'arbs' ? arbFilters : strategy === 'sb_baseline' ? sbFilters : strategy === 'market_and_implied_ev' ? aflMvFilters : strategy === 'market_value' ? mvFilters : evFilters;
    const setFilters = strategy === 'arbs' ? setArbFilters : strategy === 'sb_baseline' ? setSbFilters : strategy === 'market_and_implied_ev' ? setAflMvFilters : strategy === 'market_value' ? setMvFilters : setEvFilters;
    const [showDevigged, setShowDevigged] = useState(true);
    const [countdown, setCountdown] = useState(30);
    const [changeMap, setChangeMap] = useState({});
    const [newBetKeys, setNewBetKeys] = useState(new Set());
    const [recentNewKeys, setRecentNewKeys] = useState(new Set());
    const [unitSize, setUnitSize] = useState(20);
    const [toast, setToast] = useState(null);
    const [isAddingBet, setIsAddingBet] = useState(false);
    const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false);
    const [liveFeedEnabled, setLiveFeedEnabled] = useState(false);
    const [liveFeedSnapshot, setLiveFeedSnapshot] = useState(new Set());
    const [liveFeedHidden, setLiveFeedHidden] = useState(new Set());
    const strategyOptions = STRATEGY_OPTIONS_BY_SPORT[sport] || STRATEGY_OPTIONS_BY_SPORT.nba;
    const countdownRef = useRef(null);
    const refreshIntervalRef = useRef(null);
    const prevDataRef = useRef(null);
    const highlightTimerRef = useRef(null);
    const isFirstLoad = useRef(true);

    const fetchOddsData = useCallback(async (selectedSport, selectedStrategy, showLoading = false) => {
        if (showLoading) setIsLoading(true);
        setError(null);

        const result = await oddsDataService.getOddsData(selectedSport, selectedStrategy);

        if (result.success) {
            const data = result.data;
            const bets = data?.bets || data?.arbs || [];

            if (!isFirstLoad.current && prevDataRef.current && bets.length > 0) {
                const { changes, newKeys } = computeChanges(prevDataRef.current, bets, selectedStrategy);

                setChangeMap(changes);
                setNewBetKeys(newKeys);
                setRecentNewKeys(newKeys);

                if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
                if (Object.keys(changes).length > 0 || newKeys.size > 0) {
                    highlightTimerRef.current = setTimeout(() => {
                        setChangeMap({});
                        setNewBetKeys(new Set());
                    }, HIGHLIGHT_DURATION);
                }
            } else {
                isFirstLoad.current = false;
            }

            prevDataRef.current = bets;

            if (bets.length > 0) {
                setOddsData(bets);
                setLastUpdated(data.lastUpdated);
            } else {
                setOddsData([]);
                setLastUpdated(data?.lastUpdated || null);
            }
            setMeta(result.meta || null);
        } else {
            setOddsData(null);
            setLastUpdated(null);
            setMeta(null);
            setError(result);

            if (result.isSubscriptionError || result.isAuthError) {
                if (countdownRef.current) clearInterval(countdownRef.current);
                if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
            }
        }

        setIsLoading(false);
    }, []);

    useEffect(() => {
        fetchOddsData(sport, strategy, true);
        setCountdown(10);

        if (countdownRef.current) clearInterval(countdownRef.current);
        if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);

        countdownRef.current = setInterval(() => {
            setCountdown(prev => (prev <= 1 ? 10 : prev - 1));
        }, 1000);

        refreshIntervalRef.current = setInterval(() => {
            fetchOddsData(sport, strategy);
        }, 10000);

        return () => {
            clearInterval(countdownRef.current);
            clearInterval(refreshIntervalRef.current);
            if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
        };
    }, [sport, strategy, fetchOddsData]);

    useEffect(() => {
        const fetchSettings = async () => {
            const result = await resultsTrackerService.getSettings();
            if (result.success && result.data) {
                const { advancedUnitSettings, monthlyUnits, unitSize: defaultUnit } = result.data;
                if (advancedUnitSettings && monthlyUnits?.length > 0) {
                    const now = new Date();
                    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                    const match = monthlyUnits
                        .filter(m => m.startDate?.substring(0, 7) <= currentMonth)
                        .sort((a, b) => b.startDate.localeCompare(a.startDate))[0];
                    setUnitSize(match?.unitSize || defaultUnit || 20);
                } else {
                    setUnitSize(defaultUnit || 20);
                }
            }
        };
        fetchSettings();
    }, []);

    const showToast = useCallback((message, type = 'error', duration = 5000) => {
        setToast({ message, type, duration });
    }, []);

    const handleAddToTracker = useCallback(async (betPayload) => {
        setIsAddingBet(true);
        const result = await resultsTrackerService.addBet(betPayload);
        if (result.success) {
            showToast('Bet added to tracker successfully', 'success');
            setTrackerModalBetKey(null);
        } else {
            if (result.isAuthError) {
                showToast('Session expired. Please log in again.', 'error');
            } else {
                showToast(result.message || 'Failed to add bet to tracker', 'error');
            }
        }
        setIsAddingBet(false);
        return result;
    }, [showToast]);

    const handleSportChange = (e) => {
        const newSport = e.target.value;
        setSport(newSport);
        setStrategy(DEFAULT_STRATEGY_BY_SPORT[newSport] || 'player_line');
        setSortConfig({ key: null, direction: 'desc' });
        prevDataRef.current = null;
        isFirstLoad.current = true;
        setChangeMap({});
        setNewBetKeys(new Set());
        setRecentNewKeys(new Set());
        setLiveFeedEnabled(false);
        setLiveFeedSnapshot(new Set());
        setLiveFeedHidden(new Set());
        setExpandedBetKeys(new Set());
        setSelectedGame('all');
    };

    const handleStrategyChange = (e) => {
        const newStrategy = e.target.value;
        setStrategy(newStrategy);
        setSortConfig({ key: null, direction: 'desc' });
        prevDataRef.current = null;
        isFirstLoad.current = true;
        setChangeMap({});
        setNewBetKeys(new Set());
        setRecentNewKeys(new Set());
        setLiveFeedEnabled(false);
        setLiveFeedSnapshot(new Set());
        setLiveFeedHidden(new Set());
        setExpandedBetKeys(new Set());
    };

    const handleRefresh = () => {
        fetchOddsData(sport, strategy);
        setCountdown(10);

        if (countdownRef.current) clearInterval(countdownRef.current);
        if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);

        countdownRef.current = setInterval(() => {
            setCountdown(prev => (prev <= 1 ? 10 : prev - 1));
        }, 1000);

        refreshIntervalRef.current = setInterval(() => {
            fetchOddsData(sport, strategy);
        }, 10000);
    };

    const handleSort = useCallback((key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
        }));
    }, []);

    // Build list of all bookmakers across all bets for EV strategy (for dynamic columns)
    const allBookmakers = useMemo(() => {
        if (!oddsData || oddsData.length === 0) return [];

        const bookmakerSet = new Set();
        oddsData.forEach(bet => {
            if (strategy === 'player_line' && bet.bookmakerData) {
                bet.bookmakerData.forEach(b => bookmakerSet.add(b.bookmaker));
            } else if (MV_STRATEGIES.has(strategy) && bet.bookmakerOdds) {
                bet.bookmakerOdds.forEach(b => bookmakerSet.add(b.bookmaker));
            } else if (strategy === 'arbs') {
                if (bet.overBookmaker) bookmakerSet.add(bet.overBookmaker);
                if (bet.underBookmaker) bookmakerSet.add(bet.underBookmaker);
            }
        });

        return KNOWN_BOOKMAKERS.filter(b => bookmakerSet.has(b)).concat(
            [...bookmakerSet].filter(b => !KNOWN_BOOKMAKERS.includes(b)).sort()
        );
    }, [oddsData, strategy]);

    const maxBookmakers = useMemo(() => {
        if (!oddsData || oddsData.length === 0) return KNOWN_BOOKMAKERS.length;
        let max = 0;
        oddsData.forEach(bet => {
            const count = strategy === 'player_line'
                ? (bet.bookmakerData?.length || 0)
                : (bet.bookmakerOdds?.length || 0);
            if (count > max) max = count;
        });
        return Math.max(max, 2);
    }, [oddsData, strategy]);

    const trackerModalBet = useMemo(() => {
        if (!trackerModalBetKey || !oddsData) return null;
        return oddsData.find(bet => getBetKey(bet, strategy) === trackerModalBetKey) || null;
    }, [trackerModalBetKey, oddsData, strategy]);

    // Derive list of games present in the current payload (sorted, deduped).
    // We use the full `oddsData` (not the filtered set) so the dropdown always
    // shows every match a user could pivot to, even if the current text/filter
    // search has narrowed the visible rows down.
    const availableGames = useMemo(() => {
        if (!oddsData) return [];
        const set = new Set();
        for (const bet of oddsData) {
            if (bet?.match) set.add(bet.match);
        }
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [oddsData]);

    // If the selected game disappears from the data (sport change, refresh,
    // game starts and gets dropped, etc.), fall back to "all" so the user
    // doesn't get an inadvertently empty table.
    useEffect(() => {
        if (selectedGame !== 'all' && !availableGames.includes(selectedGame)) {
            setSelectedGame('all');
        }
    }, [selectedGame, availableGames]);

    const getFairOdds = useCallback((bet) => {
        if (devig === 'snipeaio') return bet.avgFairOdds;

        const comboBooks = COMBO_DEVIG_BOOKMAKERS[devig];
        if (comboBooks) {
            const bkEntries = comboBooks
                .map(bk => bet.bookmakerData?.find(bd => bd.bookmaker === bk))
                .filter(Boolean);
            if (bkEntries.length === 0) return null;
            const isOver = bet.selection === 'OVER';
            const probs = bkEntries.map(bd => {
                const devPrice = isOver ? bd.devigged_over_price : bd.devigged_under_price;
                return devPrice && devPrice > 0 ? 1 / devPrice : null;
            }).filter(p => p !== null);
            if (probs.length === 0) return null;
            const avgProb = probs.reduce((sum, p) => sum + p, 0) / probs.length;
            return avgProb > 0 ? 1 / avgProb : null;
        }

        const bkData = bet.bookmakerData?.find(bd => bd.bookmaker === devig);
        if (!bkData) return null;
        return bet.selection === 'OVER' ? bkData.devigged_over_price : bkData.devigged_under_price;
    }, [devig]);

    const getBestBook = useCallback((bet) => {
        if (!bet.bookmakerData || bet.bookmakerData.length === 0) {
            return { bookmaker: bet.bestBookmaker, odds: bet.bestOdds };
        }
        const comboBooks = COMBO_DEVIG_BOOKMAKERS[devig];
        const excludeSet = comboBooks ? new Set(comboBooks) : null;
        let best = { bookmaker: null, odds: 0 };
        bet.bookmakerData.forEach(bd => {
            if (US_BOOKMAKERS.has(bd.bookmaker)) return;
            if (devig !== 'snipeaio') {
                if (excludeSet ? excludeSet.has(bd.bookmaker) : bd.bookmaker === devig) return;
            }
            const price = bet.selection === 'OVER' ? bd.over_price : bd.under_price;
            if (price && price > best.odds) {
                best = { bookmaker: bd.bookmaker, odds: price };
            }
        });
        if (!best.bookmaker) {
            return { bookmaker: bet.bestBookmaker, odds: bet.bestOdds };
        }
        return best;
    }, [devig]);

    const getEv = useCallback((bet) => {
        if (devig === 'snipeaio') return bet.expectedValue;
        const fairOdds = getFairOdds(bet);
        const { odds: bestOdds } = getBestBook(bet);
        if (!fairOdds || fairOdds <= 0 || !bestOdds) return null;
        return (bestOdds / fairOdds - 1) * 100;
    }, [devig, getFairOdds, getBestBook]);

    const filteredData = useMemo(() => {
        if (!oddsData || oddsData.length === 0) return [];
        let data = oddsData;

        if (strategy === 'player_line') {
            if (filters.minBookmakers.enabled) {
                data = data.filter(bet => (bet.bookmakerData?.length || 0) >= filters.minBookmakers.value);
            }
            if (filters.minEv.enabled) {
                data = data.filter(bet => (getEv(bet) ?? 0) >= filters.minEv.value);
            }
            if (filters.startWithin.enabled) {
                const cutoff = Date.now() + filters.startWithin.value * 60 * 60 * 1000;
                data = data.filter(bet => {
                    if (!bet.startTime) return false;
                    const startMs = new Date(bet.startTime).getTime();
                    return startMs <= cutoff && startMs >= Date.now();
                });
            }
            if (filters.excludeBestBookmakers.enabled && filters.excludeBestBookmakers.value.length > 0) {
                data = data.filter(bet => !filters.excludeBestBookmakers.value.includes(getBestBook(bet).bookmaker));
            }

            if (devig !== 'snipeaio') {
                const comboBooks = COMBO_DEVIG_BOOKMAKERS[devig];
                if (comboBooks) {
                    data = data.filter(bet => {
                        const bkMap = {};
                        bet.bookmakerData?.forEach(bd => { bkMap[bd.bookmaker] = bd; });
                        return comboBooks.every(bk => {
                            const bd = bkMap[bk];
                            if (!bd) return false;
                            const price = bet.selection === 'OVER' ? bd.devigged_over_price : bd.devigged_under_price;
                            return price && price > 0;
                        });
                    });
                } else {
                    data = data.filter(bet => {
                        const bd = bet.bookmakerData?.find(b => b.bookmaker === devig);
                        if (!bd) return false;
                        const price = bet.selection === 'OVER' ? bd.devigged_over_price : bd.devigged_under_price;
                        return price && price > 0;
                    });
                }
            }
        }

        if (MV_STRATEGIES.has(strategy)) {
            if (filters.minBookmakers.enabled) {
                data = data.filter(bet => (bet.bookmakerOdds?.length || 0) >= filters.minBookmakers.value);
            }
            if (filters.minEv.enabled) {
                data = data.filter(bet => Math.round((bet.valueOpportunities?.[0]?.impliedEdgePercentage ?? 0) * 10) / 10 >= filters.minEv.value);
            }
            if (filters.minImpliedEv?.enabled) {
                data = data.filter(bet => {
                    const opp = bet.valueOpportunities?.[0];
                    const fields = getImpliedFields(opp, aflSeasonView, opp?.odds);
                    if (!isImpliedValid(fields)) return false;
                    const ev = fields.impliedEv ?? -Infinity;
                    return ev >= filters.minImpliedEv.value;
                });
            }
            if (filters.minSampleSize?.enabled) {
                data = data.filter(bet => {
                    const opp = bet.valueOpportunities?.[0];
                    const { totalGames } = getImpliedFields(opp, aflSeasonView, opp?.odds);
                    return (totalGames ?? 0) >= filters.minSampleSize.value;
                });
            }
            if (filters.minOdds?.enabled) {
                const minOddsVal = parseFloat(filters.minOdds.value);
                if (!isNaN(minOddsVal)) {
                    data = data.filter(bet => {
                        const bestOdds = bet.valueOpportunities?.[0]?.odds ?? 0;
                        return bestOdds >= minOddsVal;
                    });
                }
            }
            if (filters.startWithin.enabled) {
                const cutoff = Date.now() + filters.startWithin.value * 60 * 60 * 1000;
                data = data.filter(bet => {
                    if (!bet.startTime) return false;
                    const startMs = new Date(bet.startTime).getTime();
                    return startMs <= cutoff && startMs >= Date.now();
                });
            }
            if (filters.excludeBestBookmakers.enabled && filters.excludeBestBookmakers.value.length > 0) {
                data = data.filter(bet => {
                    const bestBk = bet.valueOpportunities?.[0]?.bookmaker;
                    return !filters.excludeBestBookmakers.value.includes(bestBk);
                });
            }
        }

        if (strategy === 'arbs') {
            if (filters.minArb?.enabled) {
                data = data.filter(bet => (bet.arbPercentage ?? 0) >= filters.minArb.value);
            }
            if (filters.excludeBookmakers?.enabled && filters.excludeBookmakers.value.length > 0) {
                data = data.filter(bet =>
                    !filters.excludeBookmakers.value.includes(bet.overBookmaker) &&
                    !filters.excludeBookmakers.value.includes(bet.underBookmaker)
                );
            }
        }

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            data = data.filter(bet => bet.player?.toLowerCase().includes(term));
        }

        if (sport === 'afl' && aflPropType !== 'all') {
            data = data.filter(bet => getAflPropTypeFromMarket(bet.market) === aflPropType);
        }

        if (selectedGame !== 'all') {
            data = data.filter(bet => bet.match === selectedGame);
        }

        const seen = new Set();
        data = data.filter(bet => {
            const key = getBetKey(bet, strategy);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });

        return data;
    }, [oddsData, filters, strategy, getEv, getBestBook, searchTerm, devig, aflSeasonView, sport, aflPropType, selectedGame]);

    const handleLiveFeedToggle = useCallback(() => {
        setLiveFeedEnabled(prev => {
            if (!prev && filteredData) {
                const currentKeys = new Set(filteredData.map(bet => getBetKey(bet, strategy)));
                setLiveFeedSnapshot(currentKeys);
                setLiveFeedHidden(new Set());
            }
            return !prev;
        });
    }, [filteredData, strategy]);

    const handleLiveFeedHide = useCallback((betKey) => {
        setLiveFeedHidden(prev => new Set([...prev, betKey]));
    }, []);

    const liveFeedData = useMemo(() => {
        if (!liveFeedEnabled || !filteredData) return filteredData;
        return filteredData.filter(bet => {
            const key = getBetKey(bet, strategy);
            if (liveFeedHidden.has(key)) return false;
            return !liveFeedSnapshot.has(key);
        });
    }, [filteredData, liveFeedEnabled, liveFeedSnapshot, liveFeedHidden, strategy]);

    const displayData = liveFeedEnabled ? liveFeedData : filteredData;

    // Sort bets
    const sortedBets = useMemo(() => {
        if (!displayData || displayData.length === 0) return [];
        if (!sortConfig.key) {
            if (strategy === 'player_line') {
                return [...displayData].sort((a, b) => (getEv(b) ?? 0) - (getEv(a) ?? 0));
            }
            if (MV_STRATEGIES.has(strategy)) {
                return [...displayData].sort((a, b) =>
                    (b.valueOpportunities?.[0]?.impliedEdgePercentage ?? 0) - (a.valueOpportunities?.[0]?.impliedEdgePercentage ?? 0)
                );
            }
            if (strategy === 'arbs') {
                return [...displayData].sort((a, b) => (b.arbPercentage ?? 0) - (a.arbPercentage ?? 0));
            }
            return displayData;
        }

        return [...displayData].sort((a, b) => {
            let aVal, bVal;

            if (strategy === 'player_line') {
                switch (sortConfig.key) {
                    case 'expectedValue': aVal = getEv(a) ?? 0; bVal = getEv(b) ?? 0; break;
                    case 'bestOdds': aVal = getBestBook(a).odds ?? 0; bVal = getBestBook(b).odds ?? 0; break;
                    case 'player': aVal = a.player || ''; bVal = b.player || ''; break;
                    case 'match': aVal = a.match || ''; bVal = b.match || ''; break;
                    case 'startTime': aVal = a.startTime || ''; bVal = b.startTime || ''; break;
                    case 'fairOdds': aVal = getFairOdds(a) ?? 0; bVal = getFairOdds(b) ?? 0; break;
                    default: return 0;
                }
            } else if (MV_STRATEGIES.has(strategy)) {
                switch (sortConfig.key) {
                    case 'match': aVal = a.match || ''; bVal = b.match || ''; break;
                    case 'player': aVal = a.player || ''; bVal = b.player || ''; break;
                    case 'market': aVal = a.market || ''; bVal = b.market || ''; break;
                    case 'startTime': aVal = a.startTime || ''; bVal = b.startTime || ''; break;
                    case 'edgePercentage':
                        aVal = a.valueOpportunities?.[0]?.impliedEdgePercentage ?? 0;
                        bVal = b.valueOpportunities?.[0]?.impliedEdgePercentage ?? 0;
                        break;
                    case 'bestOdds':
                        aVal = a.valueOpportunities?.[0]?.odds ?? 0;
                        bVal = b.valueOpportunities?.[0]?.odds ?? 0;
                        break;
                    case 'avgMarketOdds':
                        aVal = a.valueOpportunities?.[0]?.avgMarketOdds ?? 0;
                        bVal = b.valueOpportunities?.[0]?.avgMarketOdds ?? 0;
                        break;
                    case 'impliedOdds': {
                        const aOpp = a.valueOpportunities?.[0];
                        const bOpp = b.valueOpportunities?.[0];
                        aVal = getImpliedFields(aOpp, aflSeasonView, aOpp?.odds).impliedOdds ?? 0;
                        bVal = getImpliedFields(bOpp, aflSeasonView, bOpp?.odds).impliedOdds ?? 0;
                        break;
                    }
                    case 'impliedEv': {
                        const aOpp = a.valueOpportunities?.[0];
                        const bOpp = b.valueOpportunities?.[0];
                        aVal = getImpliedFields(aOpp, aflSeasonView, aOpp?.odds).impliedEv ?? 0;
                        bVal = getImpliedFields(bOpp, aflSeasonView, bOpp?.odds).impliedEv ?? 0;
                        break;
                    }
                    default: return 0;
                }
            } else if (strategy === 'arbs') {
                switch (sortConfig.key) {
                    case 'arbPercentage': aVal = a.arbPercentage ?? 0; bVal = b.arbPercentage ?? 0; break;
                    case 'match': aVal = a.match || ''; bVal = b.match || ''; break;
                    case 'player': aVal = a.player || ''; bVal = b.player || ''; break;
                    case 'line': aVal = a.line ?? 0; bVal = b.line ?? 0; break;
                    case 'overOdds': aVal = a.overOdds ?? 0; bVal = b.overOdds ?? 0; break;
                    case 'underOdds': aVal = a.underOdds ?? 0; bVal = b.underOdds ?? 0; break;
                    case 'startTime': aVal = a.startTime || ''; bVal = b.startTime || ''; break;
                    default: return 0;
                }
            } else {
                switch (sortConfig.key) {
                    default: return 0;
                }
            }

            if (typeof aVal === 'string') {
                const cmp = aVal.localeCompare(bVal);
                return sortConfig.direction === 'asc' ? cmp : -cmp;
            }

            return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        });
    }, [displayData, sortConfig, strategy, getFairOdds, getEv, getBestBook, liveFeedEnabled, liveFeedData, aflSeasonView]);

    const SortIcon = ({ column }) => {
        if (sortConfig.key !== column) {
    return (
                <svg className="w-3 h-3 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
            );
        }
        return sortConfig.direction === 'asc' ? (
            <svg className="w-3 h-3 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
            </svg>
        ) : (
            <svg className="w-3 h-3 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
        );
    };

    // EV (player_line) columns — uses Hot Props' visual language: oversized header padding,
    // emerald accents on the primary metric (EV), merged Player·Game·Start "Bet" column.
    const renderEVTable = () => {
        // Numeric / non-merged columns we still expose individually.
        const numericColumns = [
            { key: 'expectedValue', label: 'EV%', sortable: true, accent: true },
            { key: 'bestOdds', label: 'Best Book', sortable: true, accent: true },
        ];
        const tailColumns = [
            { key: 'marketTypeDisplay', label: 'Prop', sortable: false },
            { key: 'line', label: 'Line', sortable: false },
            { key: 'fairOdds', label: 'Fair Odds', sortable: true },
        ];

        const bookmakerColumns = allBookmakers.map(bk => ({
            key: `bk_${bk}`,
            bookmaker: bk,
            label: BOOKMAKER_DISPLAY_NAMES[bk] || bk,
            isBookmaker: true,
        }));

        // Total columns used for the empty-state colSpan.
        const totalCols = numericColumns.length + 1 /* merged Bet */ + tailColumns.length + bookmakerColumns.length + 1 /* track */;

        const renderHeaderCell = (col) => (
            <th
                key={col.key}
                onClick={() => col.sortable && handleSort(col.key)}
                className={`px-3 py-3 text-left whitespace-nowrap ${col.sortable ? 'cursor-pointer' : ''} transition-colors`}
            >
                <div className={`flex items-center gap-1 text-[10px] font-mono font-semibold uppercase tracking-widest ${
                    col.accent ? 'text-emerald-400' : col.sortable ? 'text-slate-500 hover:text-white' : 'text-slate-500'
                }`}>
                    {col.label}
                    {col.sortable && <SortIcon column={col.key} />}
                </div>
            </th>
        );

        return (
            <>
                <thead className="sticky top-0 z-10">
                    <tr className="bg-slate-800/90 backdrop-blur-md border-b border-slate-700/50">
                        {numericColumns.map(renderHeaderCell)}
                        <BetPropHeader sortConfig={sortConfig} onSort={handleSort} />
                        {tailColumns.map(renderHeaderCell)}
                        {bookmakerColumns.map(col => {
                            const isDevig = devig !== 'snipeaio' && (COMBO_DEVIG_BOOKMAKERS[devig] ? COMBO_DEVIG_BOOKMAKERS[devig].includes(col.bookmaker) : col.bookmaker === devig);
                            return (
                                <th
                                    key={col.key}
                                    className={`px-0.5 py-3 text-center ${isDevig ? 'bg-emerald-500/10 border-x border-emerald-500/20' : ''}`}
                                    title={col.label}
                                >
                                    <div className="flex items-center justify-center">
                                        <BookmakerLogo bookmaker={col.bookmaker} size={20} />
                                    </div>
                                </th>
                            );
                        })}
                        <th className="text-center px-2 py-3 text-[10px] font-mono font-semibold text-slate-500 uppercase tracking-widest whitespace-nowrap">
                            Track
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {sortedBets.length === 0 ? (
                        <tr>
                            <td colSpan={totalCols} className="px-4 py-12 text-center text-xs font-mono text-slate-500">
                                No EV opportunities found.
                            </td>
                        </tr>
                    ) : (
                        sortedBets.map((bet, index) => {
                            const bookmakerDataMap = {};
                            bet.bookmakerData?.forEach(bd => {
                                bookmakerDataMap[bd.bookmaker] = bd;
                            });

                            const betKey = getBetKey(bet, 'player_line');
                            const betChanges = changeMap[betKey] || {};
                            const isNew = newBetKeys.has(betKey);
                            const wasRecentlyNew = recentNewKeys.has(betKey);

                            return (
                                <tr
                                    key={index}
                                    className={`group transition-all duration-500 border-b border-slate-800/40 ${
                                        isNew
                                            ? 'bg-emerald-500/10 border-l-2 border-l-emerald-400'
                                            : 'hover:bg-slate-800/20'
                                    }`}
                                >
                                    {/* EV% */}
                                    <td className="px-3 py-2.5 whitespace-nowrap">
                                        {(() => {
                                            const ev = getEv(bet);
                                            return (
                                                <div className="inline-flex items-center gap-1">
                                                    <MetricBadge
                                                        value={ev}
                                                        formatted={ev != null ? `${ev >= 0 ? '+' : ''}${ev.toFixed(1)}%` : null}
                                                    />
                                                    {betChanges.expectedValue && <ChangeIndicator direction={betChanges.expectedValue} />}
                                                </div>
                                            );
                                        })()}
                                    </td>
                                    {/* Best Book (odds + logo + external link) */}
                                    {(() => {
                                        const { bookmaker: bestBk, odds: bestPrice } = getBestBook(bet);
                                        const bestBkChange = betChanges.bestOdds || betChanges[`bk_${bestBk}`];
                                        return (
                                            <td className="px-3 py-2.5 whitespace-nowrap">
                                                <div className="flex items-center gap-1.5">
                                                    <BookmakerLogo bookmaker={bestBk} size={18} />
                                                    <span className={`text-[13px] font-mono font-bold tabular-nums ${bestBkChange ? getChangeColor(bestBkChange) : 'text-white'}`}>
                                                        {formatOdds(bestPrice)}
                                                        {bestBkChange && <ChangeIndicator direction={bestBkChange} />}
                                                    </span>
                                                    {wasRecentlyNew && (
                                                        <span className="px-1.5 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded text-[8px] font-mono font-bold uppercase tracking-widest">
                                                            New
                                                        </span>
                                                    )}
                                                    {bet.bookmakerUrls?.[bestBk] && (
                                                        <a
                                                            href={bet.bookmakerUrls[bestBk]}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-slate-500 hover:text-emerald-400 transition-colors"
                                                            title="Open market"
                                                        >
                                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                            </svg>
                                                        </a>
                                                    )}
                                                </div>
                                            </td>
                                        );
                                    })()}
                                    {/* Bet (Player · Game · Start) — merged column matching Hot Props */}
                                    <BetPropCell
                                        player={bet.player}
                                        match={bet.match}
                                        startTime={bet.startTime}
                                        sport={sport}
                                        mobileMarket={bet.marketTypeDisplay}
                                    />
                                    {/* Prop */}
                                    <td className="px-3 py-2.5 text-xs font-mono text-slate-400 whitespace-nowrap">
                                        <div className="max-w-[100px] truncate" title={bet.marketTypeDisplay}>{bet.marketTypeDisplay}</div>
                                    </td>
                                    {/* Line (with selection merged, e.g. o5.5 / u8.5) */}
                                    <td className="px-3 py-2.5 whitespace-nowrap">
                                        <span className={`text-[13px] font-mono font-medium tabular-nums ${betChanges.line ? getChangeColor(betChanges.line) : 'text-slate-300'}`}>
                                            {formatLineWithSelection(bet.line, bet.selection)}
                                            {betChanges.line && <ChangeIndicator direction={betChanges.line} />}
                                        </span>
                                    </td>
                                    {/* Fair Odds */}
                                    <td className="px-3 py-2.5 whitespace-nowrap">
                                        <span className={`text-[13px] font-mono font-medium tabular-nums ${betChanges.avgFairOdds ? getChangeColor(betChanges.avgFairOdds) : 'text-slate-400'}`}>
                                            {formatOdds(getFairOdds(bet))}
                                            {betChanges.avgFairOdds && <ChangeIndicator direction={betChanges.avgFairOdds} />}
                                        </span>
                                    </td>
                                    {/* Dynamic bookmaker odds columns */}
                                    {allBookmakers.map(bk => {
                                        const bd = bookmakerDataMap[bk];
                                        const { bookmaker: currentBestBk } = getBestBook(bet);
                                        const isBest = bk === currentBestBk;
                                        const offeredPrice = bet.selection === 'OVER' ? bd?.over_price : bd?.under_price;
                                        const deviggedPrice = bet.selection === 'OVER' ? bd?.devigged_over_price : bd?.devigged_under_price;
                                        const bkChange = betChanges[`bk_${bk}`];
                                        const isDevig = devig !== 'snipeaio' && (COMBO_DEVIG_BOOKMAKERS[devig] ? COMBO_DEVIG_BOOKMAKERS[devig].includes(bk) : bk === devig);
                                        return (
                                            <td key={bk} className={`px-0.5 py-2.5 text-center ${isDevig ? 'bg-emerald-500/5 border-x border-emerald-500/20' : ''}`}>
                                                {bd ? (
                                                    <BookmakerLink bookmaker={bk} urls={bet.bookmakerUrls}>
                                                        <div className="flex flex-col items-center">
                                                            <span className={`text-[13px] font-mono tabular-nums leading-tight ${
                                                                bkChange
                                                                    ? getChangeColor(bkChange)
                                                                    : isBest ? 'text-emerald-400 font-semibold' : isDevig ? 'text-emerald-300' : 'text-slate-400'
                                                            }`}>
                                                                {formatOdds(offeredPrice)}
                                                                {bkChange && <ChangeIndicator direction={bkChange} />}
                                                            </span>
                                                            {showDevigged && deviggedPrice && (
                                                                <span className="text-[10px] font-mono text-slate-500 leading-tight mt-0.5">
                                                                    {formatOdds(deviggedPrice)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </BookmakerLink>
                                                ) : (
                                                    <span className="text-xs text-slate-700">-</span>
                                                )}
                                            </td>
                                        );
                                    })}
                                    {/* Track */}
                                    <td className="px-2 py-2.5 text-center whitespace-nowrap">
                                        <button
                                            type="button"
                                            onClick={() => setTrackerModalBetKey(getBetKey(bet, 'player_line'))}
                                            className="px-2.5 py-1 text-[11px] font-medium font-mono text-emerald-400 border border-emerald-500/20 rounded hover:bg-emerald-500/10 transition-all duration-150 whitespace-nowrap"
                                        >
                                            + Track
                                        </button>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </>
        );
    };

    // Market Value table
    // Market Value table — same pattern as renderEVTable; merges Player·Game·Start.
    const renderMarketValueTable = () => {
        const isImpliedEv = strategy === 'market_and_implied_ev';

        const numericColumns = [
            { key: 'edgePercentage', label: 'Edge%', sortable: true, accent: true },
            ...(isImpliedEv ? [
                { key: 'impliedEv', label: 'Implied Edge %', sortable: true, accent: true },
            ] : []),
            { key: 'bestOdds', label: 'Best Book', sortable: true, accent: true },
        ];
        const impliedOddsLabel = aflSeasonView === 'combined'
            ? 'Implied Odds (2025+26)'
            : 'Implied Odds (2026)';
        const tailColumns = [
            { key: 'market', label: 'Market', sortable: true },
            { key: 'avgMarketOdds', label: strategy === 'sb_baseline' ? 'SB Price' : 'Avg Price', sortable: true },
            ...(isImpliedEv ? [
                { key: 'impliedOdds', label: impliedOddsLabel, sortable: true },
            ] : []),
        ];

        let displayBookmakers = allBookmakers;
        if (strategy === 'sb_baseline') {
            const sbBookmakers = new Set(['sportsbet']);
            sortedBets.forEach(bet => {
                const bestOpp = bet.valueOpportunities?.[0];
                if (bestOpp?.bookmaker) sbBookmakers.add(bestOpp.bookmaker);
            });
            displayBookmakers = allBookmakers.filter(bk => sbBookmakers.has(bk));
        }

        const bookmakerColumns = displayBookmakers.map(bk => ({
            key: `bk_${bk}`,
            bookmaker: bk,
            label: BOOKMAKER_DISPLAY_NAMES[bk] || bk,
            isBookmaker: true,
        }));

        // Standardized to the same cell tokens as the EV table so AFL's
        // market_and_implied_ev variant doesn't render visibly smaller text/padding
        // than the other Snipe Feed tables.
        const cellPx = 'px-3';
        const cellPy = 'py-2.5';
        const headerPy = 'py-3';
        const primaryText = 'text-[13px]';
        const secondaryText = 'text-xs';
        // AFL gets an extra "expand" column at the right edge for the price-
        // movements drawer; other MV variants leave the table layout untouched.
        const showExpandColumn = isImpliedEv;
        const totalCols = numericColumns.length + 1 /* merged Bet */ + tailColumns.length + bookmakerColumns.length + 1 /* track */ + (showExpandColumn ? 1 : 0);

        const toggleExpand = (key) => {
            setExpandedBetKeys((prev) => {
                const next = new Set(prev);
                if (next.has(key)) next.delete(key);
                else next.add(key);
                return next;
            });
        };

        const renderHeaderCell = (col) => (
            <th
                key={col.key}
                onClick={() => col.sortable && handleSort(col.key)}
                className={`${cellPx} ${headerPy} text-left whitespace-nowrap ${col.sortable ? 'cursor-pointer' : ''} transition-colors`}
            >
                <div className={`flex items-center gap-1 text-[10px] font-mono font-semibold uppercase tracking-widest ${
                    col.accent ? 'text-emerald-400' : col.sortable ? 'text-slate-500 hover:text-white' : 'text-slate-500'
                }`}>
                    {col.label}
                    {col.sortable && <SortIcon column={col.key} />}
                </div>
            </th>
        );

        return (
            <>
                <thead className="sticky top-0 z-10">
                    <tr className="bg-slate-800/90 backdrop-blur-md border-b border-slate-700/50">
                        {numericColumns.map(renderHeaderCell)}
                        <BetPropHeader sortConfig={sortConfig} onSort={handleSort} />
                        {tailColumns.map(renderHeaderCell)}
                        {bookmakerColumns.map(col => (
                            <th
                                key={col.key}
                                className={`px-0.5 ${headerPy} text-center`}
                                title={col.label}
                            >
                                <div className="flex items-center justify-center">
                                    <BookmakerLogo bookmaker={col.bookmaker} size={20} />
                                </div>
                            </th>
                        ))}
                        <th className={`text-center px-2 ${headerPy} text-[10px] font-mono font-semibold text-slate-500 uppercase tracking-widest whitespace-nowrap`}>
                            Track
                        </th>
                        {showExpandColumn && (
                            <th className={`px-1 ${headerPy} w-8`} aria-label="Expand row" />
                        )}
                    </tr>
                </thead>
                <tbody>
                    {sortedBets.length === 0 ? (
                        <tr>
                            <td colSpan={totalCols} className="px-4 py-12 text-center text-xs font-mono text-slate-500">
                                {liveFeedEnabled ? (
                                    <div className="flex flex-col items-center gap-2">
                                        <svg className="w-8 h-8 text-emerald-500/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        <span className="text-slate-400">Watching for new opportunities...</span>
                                        <span className="text-xs text-slate-600">New bets will appear here as they are detected</span>
                                    </div>
                                ) : 'No market value opportunities found.'}
                            </td>
                        </tr>
                    ) : (
                        sortedBets.map((bet, index) => {
                            const opp = bet.valueOpportunities?.[0];
                            if (!opp) return null;

                            const bkOddsMap = {};
                            bet.bookmakerOdds?.forEach(bo => { bkOddsMap[bo.bookmaker] = bo; });

                            const betKey = getBetKey(bet, strategy);
                            const betChanges = changeMap[betKey] || {};
                            const isNew = newBetKeys.has(betKey);
                            const wasRecentlyNew = recentNewKeys.has(betKey);

                            const bestBk = opp.bookmaker;
                            const isExpanded = showExpandColumn && expandedBetKeys.has(betKey);
                            const implied = isImpliedEv ? getImpliedFields(opp, aflSeasonView, opp?.odds) : null;
                            const impliedValid = implied ? isImpliedValid(implied) : false;

                            return (
                                <React.Fragment key={betKey || index}>
                                <tr
                                    className={`group transition-all duration-500 border-b border-slate-800/40 ${
                                        isNew
                                            ? 'bg-emerald-500/10 border-l-2 border-l-emerald-400'
                                            : isExpanded ? 'bg-slate-800/20' : 'hover:bg-slate-800/20'
                                    }`}
                                >
                                    {/* Edge% */}
                                    <td className={`${cellPx} ${cellPy} whitespace-nowrap`}>
                                        <div className="inline-flex items-center gap-1">
                                            <MetricBadge
                                                value={opp.impliedEdgePercentage ?? null}
                                                formatted={opp.impliedEdgePercentage != null ? `+${opp.impliedEdgePercentage.toFixed(1)}%` : null}
                                            />
                                            {betChanges.edgePercentage && <ChangeIndicator direction={betChanges.edgePercentage} />}
                                        </div>
                                    </td>
                                    {/* Implied EV (AFL - after Edge%) */}
                                    {isImpliedEv && (
                                        <td className={`${cellPx} ${cellPy} whitespace-nowrap text-center`}>
                                            {impliedValid && implied.impliedEv != null ? (
                                                <MetricBadge
                                                    value={implied.impliedEv}
                                                    formatted={`${implied.impliedEv >= 0 ? '+' : ''}${implied.impliedEv.toFixed(1)}%`}
                                                />
                                            ) : <span className={`${primaryText} text-slate-700`}>—</span>}
                                        </td>
                                    )}
                                    {/* Best Book */}
                                    <td className={`${cellPx} ${cellPy} whitespace-nowrap`}>
                                        <div className="flex items-center gap-1.5">
                                            <BookmakerLogo bookmaker={bestBk} size={18} />
                                            <span className={`${primaryText} font-mono font-bold tabular-nums ${betChanges.bestOdds ? getChangeColor(betChanges.bestOdds) : 'text-white'}`}>
                                                {formatOdds(opp.odds)}
                                                {betChanges.bestOdds && <ChangeIndicator direction={betChanges.bestOdds} />}
                                            </span>
                                            {wasRecentlyNew && (
                                                <span className="px-1.5 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded text-[8px] font-mono font-bold uppercase tracking-widest">
                                                    New
                                                </span>
                                            )}
                                            {bet.bookmakerUrls?.[bestBk] && (
                                                <a
                                                    href={bet.bookmakerUrls[bestBk]}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-slate-500 hover:text-emerald-400 transition-colors"
                                                    title="Open market"
                                                >
                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                    </svg>
                                                </a>
                                            )}
                                        </div>
                                    </td>
                                    {/* Bet (Player · Game · Start) — merged column matching Hot Props */}
                                    <BetPropCell
                                        player={bet.player}
                                        match={bet.match}
                                        startTime={bet.startTime}
                                        sport={sport}
                                        mobileMarket={bet.market}
                                    />
                                    {/* Market */}
                                    <td className={`${cellPx} ${cellPy} ${secondaryText} font-mono text-slate-400 whitespace-nowrap`}>
                                        {bet.market}
                                    </td>
                                    {/* Avg Price */}
                                    <td className={`${cellPx} ${cellPy} whitespace-nowrap`}>
                                        <span className={`${primaryText} font-mono font-medium tabular-nums ${betChanges.avgMarketOdds ? getChangeColor(betChanges.avgMarketOdds) : 'text-slate-400'}`}>
                                            {formatOdds(opp.avgMarketOdds)}
                                            {betChanges.avgMarketOdds && <ChangeIndicator direction={betChanges.avgMarketOdds} />}
                                        </span>
                                    </td>
                                    {/* Implied Odds with hit rate (AFL) */}
                                    {isImpliedEv && (
                                        <td className={`${cellPx} ${cellPy} whitespace-nowrap text-center`}>
                                            {impliedValid ? (
                                                <div>
                                                    <span className={`${primaryText} font-mono font-medium tabular-nums text-slate-300`}>
                                                        {formatOdds(implied.impliedOdds)}
                                                    </span>
                                                    {implied.hitRatePercentage != null && (
                                                        <div className="text-[10px] font-mono text-slate-500 leading-tight tabular-nums mt-0.5">
                                                            {implied.lineHit}/{implied.totalGames} ({parseFloat(implied.hitRatePercentage.toFixed(2))}%)
                                                        </div>
                                                    )}
                                                </div>
                                            ) : <span className={`${primaryText} text-slate-700`}>-</span>}
                                        </td>
                                    )}
                                    {/* Bookmaker odds columns */}
                                    {displayBookmakers.map(bk => {
                                        const bo = bkOddsMap[bk];
                                        const isBest = bk === bestBk;
                                        const bkChange = betChanges[`bk_${bk}`];
                                        return (
                                            <td key={bk} className={`px-0.5 ${cellPy} text-center`}>
                                                {bo ? (
                                                    <BookmakerLink bookmaker={bk} urls={bet.bookmakerUrls}>
                                                        <span className={`${primaryText} font-mono tabular-nums ${
                                                            bkChange
                                                                ? getChangeColor(bkChange)
                                                                : isBest ? 'text-emerald-400 font-semibold' : 'text-slate-400'
                                                        }`}>
                                                            {formatOdds(bo.odds)}
                                                            {bkChange && <ChangeIndicator direction={bkChange} />}
                                                        </span>
                                                    </BookmakerLink>
                                                ) : (
                                                    <span className={`${primaryText} text-slate-700`}>-</span>
                                                )}
                                            </td>
                                        );
                                    })}
                                    {/* Track */}
                                    <td className={`px-2 ${cellPy} text-center whitespace-nowrap`}>
                                        <div className="flex items-center justify-center gap-1">
                                            <button
                                                type="button"
                                                onClick={() => setTrackerModalBetKey(getBetKey(bet, strategy))}
                                                className="px-2.5 py-1 text-[11px] font-medium font-mono text-emerald-400 border border-emerald-500/20 rounded hover:bg-emerald-500/10 transition-all duration-150 whitespace-nowrap"
                                            >
                                                + Track
                                            </button>
                                            {canTip && sport === 'afl' && (
                                                <button
                                                    type="button"
                                                    onClick={() => setTipModalBet(bet)}
                                                    aria-label={`Tip ${bet.player} ${bet.market || ''}`}
                                                    title="Submit community tip"
                                                    className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium font-mono text-amber-300 border border-amber-500/30 rounded hover:bg-amber-500/10 hover:border-amber-500/50 transition-all duration-150 whitespace-nowrap"
                                                >
                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                    </svg>
                                                    Tip
                                                </button>
                                            )}
                                            {liveFeedEnabled && (
                                                <button
                                                    onClick={() => handleLiveFeedHide(betKey)}
                                                    className="p-1 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-all"
                                                    title="Hide from live feed"
                                                >
                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    {showExpandColumn && (
                                        <td className={`px-3 ${cellPy} w-8 text-center`}>
                                            <button
                                                type="button"
                                                onClick={() => toggleExpand(betKey)}
                                                aria-expanded={isExpanded}
                                                aria-label={isExpanded ? 'Hide price fluctuations' : 'Show price fluctuations'}
                                                title={isExpanded ? 'Hide price fluctuations' : 'Show price fluctuations'}
                                                className="inline-flex items-center justify-center"
                                            >
                                                <svg
                                                    className={`inline-block w-3.5 h-3.5 text-slate-500 group-hover:text-slate-300 transition-all ${
                                                        isExpanded ? 'rotate-180 text-emerald-400' : ''
                                                    }`}
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </button>
                                        </td>
                                    )}
                                </tr>
                                {isExpanded && (
                                    <tr className="bg-slate-950/60 border-b-2 border-slate-800/80 animate-fade-in">
                                        <td colSpan={totalCols} className="p-4 md:p-5">
                                            <PriceMovementsExpanded
                                                player={bet.player}
                                                market={bet.market}
                                                sport={sport}
                                            />
                                        </td>
                                    </tr>
                                )}
                                </React.Fragment>
                            );
                        })
                    )}
                </tbody>
            </>
        );
    };

    const calcArbStakes = useCallback((overOdds, underOdds, total) => {
        if (!overOdds || !underOdds || !total || total <= 0) return { overStake: 0, underStake: 0, profit: 0 };
        const invSum = 1 / overOdds + 1 / underOdds;
        const overStake = total * (1 / overOdds) / invSum;
        const underStake = total - overStake;
        const profit = overStake * overOdds - total;
        return { overStake, underStake, profit };
    }, []);

    // Arbs table — uses BetPropCell for the merged player/game/start column.
    // Note: arbs use Line (no over/under selection merged like EV) so we keep Line as its own col.
    const renderArbsTable = () => {
        const numericColumns = [
            { key: 'arbPercentage', label: 'Arb%', sortable: true, accent: true },
        ];
        const tailColumns = [
            { key: 'marketTypeDisplay', label: 'Prop', sortable: false },
            { key: 'line', label: 'Line', sortable: true },
            { key: 'overOdds', label: 'Over', sortable: true },
            { key: 'underOdds', label: 'Under', sortable: true },
            { key: 'overStake', label: 'Over Stake', sortable: false },
            { key: 'underStake', label: 'Under Stake', sortable: false },
            { key: 'profit', label: 'Profit', sortable: false, accent: true },
        ];
        const totalCols = numericColumns.length + 1 /* merged Bet */ + tailColumns.length + 1 /* track */;

        const renderHeaderCell = (col) => (
            <th
                key={col.key}
                onClick={() => col.sortable && handleSort(col.key)}
                className={`px-3 py-3 text-left whitespace-nowrap ${col.sortable ? 'cursor-pointer' : ''} transition-colors`}
            >
                <div className={`flex items-center gap-1 text-[10px] font-mono font-semibold uppercase tracking-widest ${
                    col.accent ? 'text-emerald-400' : col.sortable ? 'text-slate-500 hover:text-white' : 'text-slate-500'
                }`}>
                    {col.label}
                    {col.sortable && <SortIcon column={col.key} />}
                </div>
            </th>
        );

        return (
            <>
                <thead className="sticky top-0 z-10">
                    {/* Stake control row — restyled to match Hot Props' command-bar tokens. */}
                    <tr className="bg-slate-800/90 backdrop-blur-md border-b border-slate-700/30">
                        <th colSpan={totalCols} className="px-3 py-2.5">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[10px] font-mono text-slate-500 uppercase font-semibold tracking-widest">Total Stake</span>
                                <div className="relative">
                                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-xs font-mono">$</span>
                                    <input
                                        type="number"
                                        min="1"
                                        step="1"
                                        value={arbStake}
                                        onChange={(e) => setArbStake(Math.max(1, Number(e.target.value) || 1))}
                                        className="pl-5 pr-2 py-1 w-24 bg-slate-800 border border-slate-700 rounded text-xs text-white font-mono tabular-nums focus:outline-none focus:border-emerald-500 transition-colors"
                                    />
                                </div>
                                {[50, 100, 200, 500].map(v => (
                                    <button
                                        key={v}
                                        onClick={() => setArbStake(v)}
                                        className={`px-2 py-1 rounded text-[11px] font-mono font-medium border transition-all tabular-nums ${
                                            arbStake === v
                                                ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/40'
                                                : 'bg-slate-900 text-slate-400 border-slate-700 hover:text-white hover:border-slate-600'
                                        }`}
                                    >
                                        ${v}
                                    </button>
                                ))}
                            </div>
                        </th>
                    </tr>
                    <tr className="bg-slate-800/90 backdrop-blur-md border-b border-slate-700/50">
                        {numericColumns.map(renderHeaderCell)}
                        <BetPropHeader sortConfig={sortConfig} onSort={handleSort} />
                        {tailColumns.map(renderHeaderCell)}
                        <th className="text-center px-2 py-3 text-[10px] font-mono font-semibold text-slate-500 uppercase tracking-widest whitespace-nowrap">
                            Track
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {sortedBets.length === 0 ? (
                        <tr>
                            <td colSpan={totalCols} className="px-4 py-12 text-center text-xs font-mono text-slate-500">
                                No arbitrage opportunities found.
                            </td>
                        </tr>
                    ) : (
                        sortedBets.map((bet, index) => {
                            const betKey = getBetKey(bet, 'arbs');
                            const betChanges = changeMap[betKey] || {};
                            const isNew = newBetKeys.has(betKey);
                            const wasRecentlyNew = recentNewKeys.has(betKey);
                            const { overStake, underStake, profit } = calcArbStakes(bet.overOdds, bet.underOdds, arbStake);

                            return (
                                <tr
                                    key={index}
                                    className={`group transition-all duration-500 border-b border-slate-800/40 ${
                                        isNew
                                            ? 'bg-emerald-500/10 border-l-2 border-l-emerald-400'
                                            : 'hover:bg-slate-800/20'
                                    }`}
                                >
                                    {/* Arb% */}
                                    <td className="px-3 py-2.5 whitespace-nowrap">
                                        <div className="inline-flex items-center gap-1">
                                            <MetricBadge
                                                value={bet.arbPercentage ?? null}
                                                formatted={bet.arbPercentage != null ? `${bet.arbPercentage >= 0 ? '+' : ''}${bet.arbPercentage.toFixed(2)}%` : null}
                                            />
                                            {betChanges.arbPercentage && <ChangeIndicator direction={betChanges.arbPercentage} />}
                                        </div>
                                    </td>
                                    {/* Bet (Player · Game · Start) — merged column matching Hot Props */}
                                    <BetPropCell
                                        player={bet.player}
                                        match={bet.match}
                                        startTime={bet.startTime}
                                        sport={sport}
                                        mobileMarket={bet.marketTypeDisplay}
                                    />
                                    {/* Prop */}
                                    <td className="px-3 py-2.5 text-xs font-mono text-slate-400 whitespace-nowrap">
                                        <div className="max-w-[100px] truncate" title={bet.marketTypeDisplay}>{bet.marketTypeDisplay}</div>
                                    </td>
                                    {/* Line */}
                                    <td className="px-3 py-2.5 whitespace-nowrap">
                                        <span className="text-[13px] font-mono font-medium tabular-nums text-slate-300">{bet.line}</span>
                                    </td>
                                    {/* Over (odds + bookmaker) */}
                                    <td className="px-3 py-2.5 whitespace-nowrap">
                                        <div className="flex flex-col gap-0.5">
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-[10px] font-mono text-slate-500 uppercase font-semibold tracking-wider">O</span>
                                                <BookmakerLogo bookmaker={bet.overBookmaker} size={16} />
                                                <span className={`text-[13px] font-mono font-bold tabular-nums ${betChanges.overOdds ? getChangeColor(betChanges.overOdds) : 'text-white'}`}>
                                                    {formatOdds(bet.overOdds)}
                                                    {betChanges.overOdds && <ChangeIndicator direction={betChanges.overOdds} />}
                                                </span>
                                                {wasRecentlyNew && (
                                                    <span className="px-1.5 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded text-[8px] font-mono font-bold uppercase tracking-widest">
                                                        New
                                                    </span>
                                                )}
                                                {bet.bookmakerUrls?.[bet.overBookmaker] && (
                                                    <a
                                                        href={bet.bookmakerUrls[bet.overBookmaker]}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-slate-500 hover:text-emerald-400 transition-colors"
                                                        title="Open market"
                                                    >
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                        </svg>
                                                    </a>
                                                )}
                                            </div>
                                            {bet.overSource === 'prop' && bet.overSelection && (
                                                <div className="text-[10px] font-mono text-slate-500 truncate max-w-[140px] pl-4" title={bet.overSelection}>
                                                    {bet.overSelection}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    {/* Under (odds + bookmaker) */}
                                    <td className="px-3 py-2.5 whitespace-nowrap">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[10px] font-mono text-slate-500 uppercase font-semibold tracking-wider">U</span>
                                            <BookmakerLogo bookmaker={bet.underBookmaker} size={16} />
                                            <span className={`text-[13px] font-mono font-bold tabular-nums ${betChanges.underOdds ? getChangeColor(betChanges.underOdds) : 'text-white'}`}>
                                                {formatOdds(bet.underOdds)}
                                                {betChanges.underOdds && <ChangeIndicator direction={betChanges.underOdds} />}
                                            </span>
                                            {bet.bookmakerUrls?.[bet.underBookmaker] && (
                                                <a
                                                    href={bet.bookmakerUrls[bet.underBookmaker]}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-slate-500 hover:text-emerald-400 transition-colors"
                                                    title="Open market"
                                                >
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                    </svg>
                                                </a>
                                            )}
                                        </div>
                                    </td>
                                    {/* Over Stake */}
                                    <td className="px-3 py-2.5 whitespace-nowrap">
                                        <span className="text-[13px] font-mono font-medium tabular-nums text-slate-300">${overStake.toFixed(2)}</span>
                                    </td>
                                    {/* Under Stake */}
                                    <td className="px-3 py-2.5 whitespace-nowrap">
                                        <span className="text-[13px] font-mono font-medium tabular-nums text-slate-300">${underStake.toFixed(2)}</span>
                                    </td>
                                    {/* Profit */}
                                    <td className="px-3 py-2.5 whitespace-nowrap">
                                        <span className="text-[13px] font-mono font-bold tabular-nums text-emerald-400">
                                            +${profit.toFixed(2)}
                                        </span>
                                    </td>
                                    {/* Track */}
                                    <td className="px-2 py-2.5 text-center whitespace-nowrap">
                                        <button
                                            type="button"
                                            onClick={() => setTrackerModalBetKey(getBetKey(bet, 'arbs'))}
                                            className="px-2.5 py-1 text-[11px] font-medium font-mono text-emerald-400 border border-emerald-500/20 rounded hover:bg-emerald-500/10 transition-all duration-150 whitespace-nowrap"
                                        >
                                            + Track
                                        </button>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </>
        );
    };

    const betCount = oddsData?.length ?? 0;

    return (
        <div className="flex flex-col p-2 md:p-4 h-[calc(100vh-72px)] overflow-hidden">
            {/* Main Card */}
            <div className="bg-slate-900/50 rounded-xl md:rounded-2xl border border-slate-700/50 overflow-hidden relative flex flex-col flex-1 min-h-0">
                {/* Initial load overlay */}
                {isLoading && !oddsData && (
                    <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm z-10 flex items-center justify-center">
                        <div className="flex flex-col items-center gap-3">
                            <div className="flex gap-1">
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" style={{ animationDelay: '0ms' }} />
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" style={{ animationDelay: '200ms' }} />
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" style={{ animationDelay: '400ms' }} />
                            </div>
                            <span className="text-xs font-mono text-slate-500">Loading feed data</span>
                        </div>
                    </div>
                )}

                {/* Refresh overlay */}
                {isLoading && oddsData && (
                    <div className="absolute inset-0 bg-slate-900/40 z-10 flex items-center justify-center pointer-events-none">
                        <div className="flex items-center gap-2.5 bg-slate-800 px-4 py-2.5 rounded-lg border border-slate-700/50 shadow-xl pointer-events-auto">
                            <div className="w-4 h-4 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
                            <span className="text-xs font-mono text-slate-400">Updating...</span>
                        </div>
                    </div>
                )}

                {/* Header — same command-bar pattern as Hot Props */}
                <div className="border-b border-slate-700/50">
                    {/* Desktop Header */}
                    <div className="hidden md:flex items-center justify-between px-4 py-3 gap-3">
                        <div className="flex items-center gap-3 flex-shrink-0 min-w-0">
                            {meta?.tier && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 tracking-widest uppercase">
                                    {meta.tier}
                                </span>
                            )}
                            {!isLoading && oddsData && (
                                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-slate-800/50 rounded">
                                    <span className="text-[11px] font-mono font-semibold text-white tabular-nums">{oddsData.length}</span>
                                    <span className="text-[11px] font-mono text-slate-600">found</span>
                                </div>
                            )}
                            <select
                                value={sport}
                                onChange={handleSportChange}
                                aria-label="Sport"
                                className="px-2.5 py-1 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-slate-300 focus:outline-none focus:border-emerald-500 cursor-pointer hover:border-slate-600 transition-colors"
                            >
                                {SPORT_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            {strategyOptions.length > 1 && (
                                <select
                                    value={strategy}
                                    onChange={handleStrategyChange}
                                    aria-label="Strategy"
                                    className="px-2.5 py-1 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-slate-300 focus:outline-none focus:border-emerald-500 cursor-pointer hover:border-slate-600 transition-colors max-w-[180px]"
                                >
                                    {strategyOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            )}
                            {strategy === 'player_line' && (
                                <select
                                    value={devig}
                                    onChange={(e) => setDevig(e.target.value)}
                                    aria-label="Devig source"
                                    className="px-2.5 py-1 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-slate-300 focus:outline-none focus:border-emerald-500 cursor-pointer hover:border-slate-600 transition-colors"
                                >
                                    {DEVIG_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            )}
                            {sport === 'afl' && (
                                <label className="inline-flex items-center gap-1.5">
                                    <span className="text-[10px] font-mono font-semibold text-slate-500 uppercase tracking-widest whitespace-nowrap">
                                        Prop Type
                                    </span>
                                    <select
                                        value={aflPropType}
                                        onChange={(e) => setAflPropType(e.target.value)}
                                        aria-label="Prop type"
                                        title="Filter by prop type"
                                        className="px-2.5 py-1 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-slate-300 focus:outline-none focus:border-emerald-500 cursor-pointer hover:border-slate-600 transition-colors"
                                    >
                                        {AFL_PROP_TYPE_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </label>
                            )}
                            {sport === 'afl' && strategy === 'market_and_implied_ev' && (
                                <label className="inline-flex items-center gap-1.5">
                                    <span className="inline-flex items-center gap-1 text-[10px] font-mono font-semibold text-slate-500 uppercase tracking-widest whitespace-nowrap">
                                        Dataset
                                        <InfoTooltip title="Dataset" align="right">
                                            Switches the historical sample used to calculate the
                                            <span className="text-white"> Implied Odds </span>
                                            and
                                            <span className="text-white"> Implied Edge </span>
                                            columns.
                                            <span className="block mt-2 space-y-0.5 text-slate-400">
                                                <span className="block">
                                                    <span className="text-slate-200">2026</span> — current season only.
                                                </span>
                                                <span className="block">
                                                    <span className="text-slate-200">2025 + 2026</span> — combined for a larger sample.
                                                </span>
                                            </span>
                                        </InfoTooltip>
                                    </span>
                                    <select
                                        value={aflSeasonView}
                                        onChange={(e) => setAflSeasonView(e.target.value)}
                                        aria-label="Implied stats season"
                                        title="Season(s) used for implied odds & implied edge"
                                        className="px-2.5 py-1 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-slate-300 focus:outline-none focus:border-emerald-500 cursor-pointer hover:border-slate-600 transition-colors"
                                    >
                                        {AFL_SEASON_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </label>
                            )}
                            {strategy === 'player_line' && (
                                <TogglePill
                                    active={showDevigged}
                                    onClick={() => setShowDevigged(prev => !prev)}
                                    label="Devigged"
                                />
                            )}
                        </div>

                        <div className="flex items-center gap-3 flex-shrink-0">
                            {strategy !== 'arbs' && (
                                <div className="relative">
                                    <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    <input
                                        type="text"
                                        placeholder="Player name..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-8 pr-3 py-1.5 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 w-44 hover:border-slate-600 transition-colors"
                                    />
                                </div>
                            )}
                            {availableGames.length > 0 && (
                                <GameSelect
                                    value={selectedGame}
                                    options={availableGames}
                                    onChange={setSelectedGame}
                                />
                            )}
                            {sport === 'afl' && (
                                <TogglePill
                                    active={liveFeedEnabled}
                                    onClick={handleLiveFeedToggle}
                                    label="Live Feed"
                                    count={liveFeedEnabled && liveFeedData ? liveFeedData.length : null}
                                />
                            )}
                            {(strategy === 'player_line' || MV_STRATEGIES.has(strategy) || strategy === 'arbs') && (() => {
                                const count = Object.entries(filters).filter(([, f]) => f.enabled && (!Array.isArray(f.value) || f.value.length > 0)).length;
                                return (
                                    <button
                                        type="button"
                                        onClick={() => setShowFilters(true)}
                                        aria-label="Open filters"
                                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded border transition-colors ${
                                            count > 0
                                                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                                                : 'border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-600'
                                        }`}
                                    >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h18M6 12h12M10 19h4" />
                                        </svg>
                                        <span>Filters</span>
                                        {count > 0 && (
                                            <span className="ml-0.5 min-w-[16px] h-4 px-1 rounded-full bg-emerald-400 text-emerald-950 text-[9px] font-mono font-bold leading-none inline-flex items-center justify-center tabular-nums">
                                                {count}
                                            </span>
                                        )}
                                    </button>
                                );
                            })()}
                            {!isLoading && lastUpdated && (
                                <div className="flex items-center gap-2">
                                    <span className="font-mono text-[11px] text-slate-600 tabular-nums">{new Date(lastUpdated).toLocaleTimeString()}</span>
                                    <CountdownRing countdown={countdown} total={10} />
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={handleRefresh}
                                disabled={isLoading}
                                aria-label="Refresh"
                                title="Refresh"
                                className="p-1.5 border border-slate-700 rounded text-slate-400 hover:text-emerald-400 hover:border-slate-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <svg className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Mobile Header */}
                    <div className="flex md:hidden items-center justify-between px-3 py-2.5 gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                            <h3 className="text-sm font-semibold text-white whitespace-nowrap">
                                Snipe Feed
                            </h3>
                            {meta?.tier && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-mono font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 uppercase tracking-wider">
                                    {meta.tier}
                                </span>
                            )}
                            {!isLoading && oddsData && (
                                <span className="text-[11px] font-mono text-slate-600 tabular-nums">
                                    <span className="text-slate-400 font-semibold">{oddsData.length}</span>
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            {!isLoading && lastUpdated && (
                                <CountdownRing countdown={countdown} total={10} />
                            )}
                            {(strategy === 'player_line' || MV_STRATEGIES.has(strategy) || strategy === 'arbs') && (() => {
                                const count = Object.entries(filters).filter(([, f]) => f.enabled && (!Array.isArray(f.value) || f.value.length > 0)).length;
                                return (
                                    <button
                                        type="button"
                                        onClick={() => setShowFilters(true)}
                                        aria-label="Open filters"
                                        className={`relative p-1.5 border rounded transition-colors ${
                                            count > 0
                                                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                                                : 'border-slate-700 text-slate-500 hover:text-white hover:border-slate-600'
                                        }`}
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h18M6 12h12M10 19h4" />
                                        </svg>
                                        {count > 0 && (
                                            <span className="absolute -top-1 -right-1 min-w-[14px] h-3.5 px-1 rounded-full bg-emerald-400 text-emerald-950 text-[9px] font-mono font-bold leading-none inline-flex items-center justify-center tabular-nums">
                                                {count}
                                            </span>
                                        )}
                                    </button>
                                );
                            })()}
                            <button
                                type="button"
                                onClick={handleRefresh}
                                disabled={isLoading}
                                aria-label="Refresh"
                                title="Refresh"
                                className="p-1.5 border border-slate-700 rounded text-slate-400 hover:text-emerald-400 hover:border-slate-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <svg className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                            </button>
                            <button
                                onClick={() => setMobileSettingsOpen(prev => !prev)}
                                aria-label="Toggle controls"
                                className="p-1.5 border border-slate-700 rounded text-slate-500 hover:text-white hover:border-slate-600 transition-colors"
                            >
                                <svg className={`w-4 h-4 transition-transform duration-200 ${mobileSettingsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {mobileSettingsOpen && (
                        <div className="md:hidden px-3 pb-3 border-t border-slate-700/50 pt-3 animate-fade-in space-y-3">
                            {(() => {
                                // Pick a column count that doesn't leave gaps. Selects shown:
                                // sport (always) + strategy (only when >1 option) + devig
                                // (player_line) + propType (afl) + season (afl + implied_ev).
                                // 4 selects lay out cleanest as 2 columns; 3 selects fit a
                                // 3-col row.
                                const showStrategy = strategyOptions.length > 1;
                                const selectCount = 1
                                    + (showStrategy ? 1 : 0)
                                    + (strategy === 'player_line' ? 1 : 0)
                                    + (sport === 'afl' ? 1 : 0)
                                    + (sport === 'afl' && strategy === 'market_and_implied_ev' ? 1 : 0);
                                const gridCls = selectCount === 3 ? 'grid-cols-3' : 'grid-cols-2';
                                return (
                            <div className={`grid gap-2 items-end ${gridCls}`}>
                                <select
                                    value={sport}
                                    onChange={handleSportChange}
                                    aria-label="Sport"
                                    className="px-3 py-2 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-white focus:outline-none focus:border-emerald-500"
                                >
                                    {SPORT_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                                {showStrategy && (
                                    <select
                                        value={strategy}
                                        onChange={handleStrategyChange}
                                        aria-label="Strategy"
                                        className="px-3 py-2 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-white focus:outline-none focus:border-emerald-500"
                                    >
                                        {strategyOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                )}
                                {strategy === 'player_line' && (
                                    <select
                                        value={devig}
                                        onChange={(e) => setDevig(e.target.value)}
                                        aria-label="Devig source"
                                        className="px-3 py-2 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-white focus:outline-none focus:border-emerald-500"
                                    >
                                        {DEVIG_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                )}
                                {sport === 'afl' && (
                                    <label className="flex flex-col gap-1 min-w-0">
                                        <span className="text-[9px] font-mono font-semibold text-slate-500 uppercase tracking-widest">
                                            Prop Type
                                        </span>
                                        <select
                                            value={aflPropType}
                                            onChange={(e) => setAflPropType(e.target.value)}
                                            aria-label="Prop type"
                                            className="px-3 py-2 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-white focus:outline-none focus:border-emerald-500"
                                        >
                                            {AFL_PROP_TYPE_OPTIONS.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </label>
                                )}
                                {sport === 'afl' && strategy === 'market_and_implied_ev' && (
                                    <label className="flex flex-col gap-1 min-w-0">
                                        <span className="inline-flex items-center gap-1 text-[9px] font-mono font-semibold text-slate-500 uppercase tracking-widest">
                                            Dataset
                                            <InfoTooltip title="Dataset" align="right">
                                                Switches the historical sample used to calculate the
                                                <span className="text-white"> Implied Odds </span>
                                                and
                                                <span className="text-white"> Implied Edge </span>
                                                columns.
                                                <span className="block mt-2 space-y-0.5 text-slate-400">
                                                    <span className="block">
                                                        <span className="text-slate-200">2026</span> — current season only.
                                                    </span>
                                                    <span className="block">
                                                        <span className="text-slate-200">2025 + 2026</span> — combined for a larger sample.
                                                    </span>
                                                </span>
                                            </InfoTooltip>
                                        </span>
                                        <select
                                            value={aflSeasonView}
                                            onChange={(e) => setAflSeasonView(e.target.value)}
                                            aria-label="Implied stats season"
                                            className="px-3 py-2 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-white focus:outline-none focus:border-emerald-500"
                                        >
                                            {AFL_SEASON_OPTIONS.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </label>
                                )}
                            </div>
                                );
                            })()}

                            {(() => {
                                // Compose a single 70/30 row for the player-name search and
                                // the trailing toggle pill. Live Feed (AFL) and Devigged
                                // (NBA player_line) are mutually exclusive, so at most one
                                // pill ever appears here. When only one of search/pill is
                                // visible, that one takes the full row width.
                                const showSearch = strategy !== 'arbs';
                                const showLiveFeedPill = sport === 'afl';
                                const showDevigPill = strategy === 'player_line';
                                const hasSidePill = showLiveFeedPill || showDevigPill;
                                if (!showSearch && !hasSidePill) return null;
                                const split = showSearch && hasSidePill;
                                const searchCls = split ? 'flex-[7]' : 'flex-1';
                                const pillCls = `${split ? 'flex-[3]' : 'flex-1'} justify-center`;
                                return (
                                    <div className="flex gap-2">
                                        {showSearch && (
                                            <div className={`relative min-w-0 ${searchCls}`}>
                                                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                </svg>
                                                <input
                                                    type="text"
                                                    placeholder="Player name..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    className="pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 w-full"
                                                />
                                            </div>
                                        )}
                                        {showLiveFeedPill && (
                                            <TogglePill
                                                active={liveFeedEnabled}
                                                onClick={handleLiveFeedToggle}
                                                label="Live Feed"
                                                count={liveFeedEnabled && liveFeedData ? liveFeedData.length : null}
                                                className={pillCls}
                                            />
                                        )}
                                        {showDevigPill && (
                                            <TogglePill
                                                active={showDevigged}
                                                onClick={() => setShowDevigged(prev => !prev)}
                                                label="Devigged"
                                                className={pillCls}
                                            />
                                        )}
                                    </div>
                                );
                            })()}

                            {availableGames.length > 0 && (
                                <select
                                    value={selectedGame}
                                    onChange={(e) => setSelectedGame(e.target.value)}
                                    aria-label="Filter by game"
                                    className={`w-full px-3 py-2 border rounded text-xs font-mono focus:outline-none focus:border-emerald-500 ${
                                        selectedGame === 'all'
                                            ? 'bg-slate-800 border-slate-700 text-white'
                                            : 'bg-emerald-500/10 border-emerald-500/40 text-emerald-200'
                                    }`}
                                >
                                    <option value="all">All games</option>
                                    {availableGames.map((m) => (
                                        <option key={m} value={m}>
                                            {formatGameLabel(m)}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}
                </div>

                {/* Error State */}
                {error && !isLoading && (
                    <div className="flex items-center justify-center py-24 px-6">
                        {error.isSubscriptionError ? (
                            <div className="flex flex-col items-center text-center max-w-sm">
                                <div className="w-14 h-14 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-5">
                                    <svg className="w-7 h-7 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <h4 className="text-base font-semibold font-mono text-white mb-2">Subscription Required</h4>
                                <p className="text-sm font-mono text-slate-500 leading-relaxed mb-6">
                                    An active subscription is required to access the Snipe Feed.
                                </p>
                                <a
                                    href="/manage-subscriptions"
                                    className="px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg text-sm font-semibold hover:from-emerald-500 hover:to-teal-500 transition-all flex items-center gap-2"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                    Manage Subscriptions
                                </a>
                            </div>
                        ) : (
                            (() => {
                                const errorConfig = {
                                    AUTH: { icon: 'M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636', color: 'text-red-400', bgColor: 'bg-red-500/5 border-red-500/10', title: 'Authentication Failed' },
                                    RATE_LIMIT_EXCEEDED: { icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', color: 'text-amber-400', bgColor: 'bg-amber-500/5 border-amber-500/10', title: 'Too Many Requests' },
                                    SERVICE_UNAVAILABLE: { icon: 'M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z', color: 'text-slate-400', bgColor: 'bg-slate-500/5 border-slate-500/10', title: 'Service Unavailable' },
                                    NETWORK_ERROR: { icon: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', color: 'text-red-400', bgColor: 'bg-red-500/5 border-red-500/10', title: 'Connection Error' },
                                };
                                const code = error.isAuthError ? 'AUTH' : (error.errorCode || 'NETWORK_ERROR');
                                const config = errorConfig[code] || errorConfig.NETWORK_ERROR;
                                const showRetry = ['SERVICE_UNAVAILABLE', 'NETWORK_ERROR', 'SERVER_ERROR'].includes(error.errorCode);
                                return (
                                    <div className="flex flex-col items-center text-center max-w-sm">
                                        <div className={`w-12 h-12 rounded-lg ${config.bgColor} border flex items-center justify-center mb-4`}>
                                            <svg className={`w-6 h-6 ${config.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d={config.icon} />
                                            </svg>
                                        </div>
                                        <h4 className={`text-sm font-semibold mb-1.5 ${config.color}`}>{config.title}</h4>
                                        <p className="text-xs text-slate-600 leading-relaxed">{error.message}</p>
                                        {showRetry && (
                                            <button onClick={handleRefresh} className="mt-5 px-4 py-2 text-xs font-mono font-medium text-slate-300 border border-slate-700 rounded-lg hover:bg-slate-800 hover:border-slate-600 transition-all flex items-center gap-2">
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Retry
                                            </button>
                                        )}
                                        {error.errorCode === 'RATE_LIMIT_EXCEEDED' && (
                                            <p className="mt-3 text-[10px] font-mono text-slate-700">Resets every 15 minutes</p>
                                        )}
                                    </div>
                                );
                            })()
                        )}
                    </div>
                )}

                {/* Table */}
                {!error && (
                    <div className="overflow-auto flex-1 min-h-0 styled-scrollbar">
                        <table className="w-full table-auto">
                            {strategy === 'player_line' && renderEVTable()}
                            {MV_STRATEGIES.has(strategy) && renderMarketValueTable()}
                            {strategy === 'arbs' && renderArbsTable()}
                        </table>
                    </div>
                )}

                {/* Summary Footer */}
                {!error && !isLoading && oddsData && (
                    <div className="px-4 py-2.5 border-t border-slate-700/50 flex items-center justify-between">
                        <div className="text-[11px] font-mono text-slate-600">
                            <span className="text-slate-500">{sortedBets.length}</span>
                            {filteredData.length !== oddsData.length && (
                                <span className="text-slate-700"> / {oddsData.length}</span>
                            )}
                            <span className="ml-1">opportunities</span>
                        </div>
                        <div className="flex items-center gap-3">
                            {lastUpdated && (
                                <span className="text-[10px] font-mono text-slate-700">
                                    {new Date(lastUpdated).toLocaleTimeString()}
                                </span>
                            )}
                            {meta?.tier && meta.tier !== 'ELITE' && (
                                <span className="text-[10px] font-mono text-amber-400/70">Upgrade for more</span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Add to Tracker Modal */}
            {trackerModalBet && (
                <AddToTrackerModal
                    bet={trackerModalBet}
                    onClose={() => setTrackerModalBetKey(null)}
                    bookmakerLogos={BOOKMAKER_LOGOS}
                    computedEv={getEv(trackerModalBet)}
                    computedFairOdds={getFairOdds(trackerModalBet)}
                    computedBestBook={getBestBook(trackerModalBet)}
                    strategy={strategy}
                    arbStake={arbStake}
                    unitSize={unitSize}
                    onAddToTracker={handleAddToTracker}
                    isAddingBet={isAddingBet}
                    sport={sport}
                />
            )}

            {/* Filters Modal */}
            {showFilters && (
                <FiltersModal
                    filters={filters}
                    onApply={setFilters}
                    onClose={() => setShowFilters(false)}
                    maxBookmakers={maxBookmakers}
                    bookmakers={allBookmakers}
                    bookmakerLogos={BOOKMAKER_LOGOS}
                    bookmakerNames={BOOKMAKER_DISPLAY_NAMES}
                    strategy={strategy}
                />
            )}

            {/* Community Tip Modal (AFL Snipe Feed only) */}
            <CommunityTipModal
                open={Boolean(tipModalBet)}
                tool="SNIPE_FEED"
                player={tipModalBet?.player}
                market={tipModalBet?.market}
                sport={sport}
                match={tipModalBet?.match}
                startTime={tipModalBet?.startTime}
                bookmakerLogos={BOOKMAKER_LOGOS}
                onClose={() => setTipModalBet(null)}
                onSuccess={() => {
                    showToast('Tip submitted to community feed', 'success');
                }}
                onAuthError={() => {
                    showToast('Session expired. Please log in again.', 'error');
                }}
            />

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    duration={toast.duration}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
}

export default SnipeFeed;
